import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * MesPuan Referans Ağacı Yönetimi
 *
 * Bu bileşen yalnızca referans ağacını yönetir. MesPuan cüzdanı, bakiye,
 * puan hareketi veya geçmiş kazanç kayıtlarını değiştiren bir istek içermez.
 * Cüzdan bilgileri yalnızca kullanıcı kartında salt okunur olarak gösterilir.
 *
 * Güvenli taşıma akışı:
 *   1. Kullanıcı/ekip seçilir.
 *   2. Yeni sponsor seçilir veya kartı üzerine sürüklenir.
 *   3. Sunucudan zorunlu taşıma ön izlemesi alınır.
 *   4. Gerekçe ve TAŞI doğrulaması girilir.
 *   5. Ön izleme anahtarı + ağaç sürümüyle taşıma onaylanır.
 *
 * Beklenen API kökü: https://panel.haymestasarim.com/api/mespuan
 * Endpoint yolları `endpoints` prop'u ile kod değişmeden özelleştirilebilir.
 */

const DEFAULT_API_BASE = "https://panel.haymestasarim.com/api/mespuan";
const MANAGER_EMAIL = "mesyat@admin.com";
const PAGE_SIZE = 100;



const DEFAULT_ENDPOINTS = Object.freeze({
  summary: "/admin/referral-tree/summary",
  roots: "/admin/referral-tree/roots",
  search: "/admin/referral-tree/search",
  children: (userId) => `/admin/referral-tree/users/${userId}/children`,
  details: (userId) => `/admin/referral-tree/users/${userId}`,



 team: (userId) => `/admin/referral-tree/users/${userId}/team`,
deleteUser: (userId) => `/admin/referral-tree/users/${userId}`,

deviceReset: "/admin/device-access/reset",
purgeByEmail: "/admin/account-purge/by-email",

health: "/admin/referral-tree/health",



  history: "/admin/referral-tree/history",
  movePreview: "/admin/referral-tree/moves/preview",
  moveCommit: "/admin/referral-tree/moves/commit",
  undoPreview: (moveId) => `/admin/referral-tree/moves/${moveId}/undo/preview`,
  undoCommit: (moveId) => `/admin/referral-tree/moves/${moveId}/undo/commit`,
});

const HEALTH_TYPES = {
  orphan: { label: "Sponsoru olmayan", tone: "warning", icon: "unlink" },
  missing_sponsor: {
    label: "Sponsoru olmayan",
    tone: "warning",
    icon: "unlink",
  },
  multiple_sponsors: {
    label: "Birden fazla sponsor",
    tone: "danger",
    icon: "alert",
  },
  duplicate_sponsor: {
    label: "Birden fazla sponsor",
    tone: "danger",
    icon: "alert",
  },
  cycle: { label: "Döngü riski", tone: "danger", icon: "cycle" },
  missing_code: {
    label: "Aktif kodu olmayan",
    tone: "warning",
    icon: "code",
  },
  multiple_codes: {
    label: "Birden fazla aktif kod",
    tone: "danger",
    icon: "code",
  },
  inactive_sponsor: {
    label: "Pasif sponsora bağlı",
    tone: "warning",
    icon: "userX",
  },
  broken_link: {
    label: "Bozuk bağlantı",
    tone: "danger",
    icon: "unlink",
  },
};

function cx(...values) {
  return values.filter(Boolean).join(" ");
}

function asPositiveInt(value) {
  const number = Number(value);
  return Number.isInteger(number) && number > 0 ? number : null;
}

function asNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asBoolean(value, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value === 1 || value === "1" || value === "true") return true;
  if (value === 0 || value === "0" || value === "false") return false;
  return fallback;
}

function firstDefined(...values) {
  return values.find((value) => value !== undefined && value !== null);
}

function normalizeToken(rawValue) {
  let value = String(rawValue || "").trim();
  if (!value || value.toLowerCase() === "null") return "";

  if (value.startsWith('"') && value.endsWith('"')) {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === "string") value = parsed.trim();
    } catch (_) {
      // Ham değer kullanılmaya devam edilir.
    }
  }

  value = value
    .replace(/^(Bearer\s+)+/i, "")
    .replace(/\s+/g, "")
    .trim();

  return value.split(".").length === 3 ? value : "";
}

function getStoredToken() {
  if (typeof window === "undefined") return "";
  const keys = [
    "token",
    "auth_token",
    "authToken",
    "jwt_token",
    "access_token",
    "accessToken",
  ];

  for (const storage of [window.localStorage, window.sessionStorage]) {
    for (const key of keys) {
      const token = normalizeToken(storage.getItem(key));
      if (token) return token;
    }
  }
  return "";
}

function decodeJwt(token) {
  if (!token || typeof window === "undefined") return {};
  try {
    const part = token.split(".")[1];
    const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "=",
    );
    const json = decodeURIComponent(
      window
        .atob(padded)
        .split("")
        .map((char) => `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch (_) {
    return {};
  }
}

function formatNumber(value) {
  return new Intl.NumberFormat("tr-TR").format(asNumber(value));
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function getInitials(name) {
  return String(name || "MesYat Kullanıcısı")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toLocaleUpperCase("tr-TR"))
    .join("");
}

function makeQuery(input = {}) {
  const params = new URLSearchParams();
  Object.entries(input).forEach(([key, value]) => {
    if (value !== "" && value !== null && value !== undefined) {
      params.set(key, String(value));
    }
  });
  const query = params.toString();
  return query ? `?${query}` : "";
}

function unwrapResult(result) {
  if (!result || typeof result !== "object") return result;
  if (result.data !== undefined) return result.data;
  if (result.result !== undefined) return result.result;
  return result;
}

function extractCollection(result, keys = []) {
  const data = unwrapResult(result);
  if (Array.isArray(data)) return { items: data, meta: result?.meta || {} };
  if (!data || typeof data !== "object") return { items: [], meta: {} };

  for (const key of ["items", "rows", "users", "nodes", ...keys]) {
    if (Array.isArray(data[key])) {
      return { items: data[key], meta: data.meta || result?.meta || {} };
    }
  }
  return { items: [], meta: data.meta || result?.meta || {} };
}

function normalizeWallet(raw = {}) {
  const source =
    raw.wallet && typeof raw.wallet === "object" ? raw.wallet : raw;
  const walletId = asPositiveInt(
    firstDefined(source.wallet_id, source.walletId, raw.wallet_id),
  );
  const hasWallet = Boolean(
    walletId ||
      source.wallet_status ||
      source.balance !== undefined ||
      raw.has_wallet,
  );

  return {
    exists: hasWallet,
    id: walletId,
    status: String(
      firstDefined(
        source.wallet_status,
        source.status,
        raw.wallet_status,
        "none",
      ),
    ).toLowerCase(),
    balance: asNumber(firstDefined(source.balance, raw.wallet_balance)),
    lifetimeEarned: asNumber(
      firstDefined(source.lifetime_earned, source.lifetimeEarned),
    ),
    lifetimeSpent: asNumber(
      firstDefined(source.lifetime_spent, source.lifetimeSpent),
    ),
  };
}

function normalizeNode(raw = {}) {
  const id = asPositiveInt(
    firstDefined(raw.user_id, raw.userId, raw.id, raw.invited_user_id),
  );
  if (!id) return null;

  const directCount = asNumber(
    firstDefined(
      raw.direct_count,
      raw.directCount,
      raw.direct_referrals,
      raw.child_count,
      raw.children_count,
      Array.isArray(raw.children) ? raw.children.length : 0,
    ),
  );

  return {
    id,
    name: String(
      firstDefined(
        raw.user_name,
        raw.name,
        raw.ad,
        raw.full_name,
        "MesYat Kullanıcısı",
      ),
    ).trim(),
    email: String(
      firstDefined(raw.email, raw.user_email, raw.eposta, ""),
    ).trim(),
    phone: String(firstDefined(raw.phone, raw.telefon, "")).trim(),
    active: asBoolean(firstDefined(raw.active, raw.aktif), true),
    accountStatus: String(
      firstDefined(
        raw.account_status,
        raw.user_status,
        raw.active === false ? "inactive" : "active",
      ),
    ).toLowerCase(),
    sponsorId: asPositiveInt(
      firstDefined(
        raw.sponsor_user_id,
        raw.sponsorId,
        raw.inviter_user_id,
        raw.parent_id,
      ),
    ),
    sponsorName: String(
      firstDefined(raw.sponsor_name, raw.inviter_name, raw.parent_name, ""),
    ).trim(),
    referralId: asPositiveInt(firstDefined(raw.referral_id, raw.link_id)),
    referralCodeId: asPositiveInt(
      firstDefined(raw.referral_code_id, raw.code_id),
    ),
    referralCode: String(firstDefined(raw.referral_code, raw.code, "")).trim(),
    referralStatus: String(
      firstDefined(raw.referral_status, raw.link_status, ""),
    ).toLowerCase(),
    directCount,
    teamCount: asNumber(
      firstDefined(
        raw.team_count,
        raw.total_team_count,
        raw.descendant_count,
        raw.network_count,
        directCount,
      ),
    ),
    depth: asNumber(firstDefined(raw.depth, raw.level), 0),
    joinedAt: firstDefined(raw.joined_at, raw.created_at, null),
    updatedAt: firstDefined(raw.updated_at, null),
    healthStatus: String(
      firstDefined(raw.health_status, raw.tree_status, "healthy"),
    ).toLowerCase(),
    wallet: normalizeWallet(raw),
    raw,
  };
}

function normalizeSummary(result) {
  const raw = unwrapResult(result) || {};
  const source = raw.stats || raw.summary || raw;
  return {
    totalUsers: asNumber(
      firstDefined(source.total_users, source.totalUsers, source.user_count),
    ),
    treeUsers: asNumber(
      firstDefined(source.tree_users, source.treeUsers, source.linked_users),
    ),
    rootUsers: asNumber(
      firstDefined(source.root_users, source.rootUsers, source.root_count),
    ),


 healthyUsers: asNumber(
  firstDefined(source.healthy_users, source.healthyUsers),
),

// Eski toplam - uyumluluk için duruyor.
issueCount: asNumber(
  firstDefined(
    source.issue_count,
    source.issueCount,
    source.problem_count,
  ),
),

// Gerçek kritik ağaç sorunları
criticalIssueCount: asNumber(
  firstDefined(
    source.critical_issue_count,
    source.criticalIssueCount,
    0,
  ),
),

// Daha düşük önemdeki uyarılar
warningIssueCount: asNumber(
  firstDefined(
    source.warning_issue_count,
    source.warningIssueCount,
    0,
  ),
),

// Kök / bilgilendirme kayıtları
informationalIssueCount: asNumber(
  firstDefined(
    source.informational_issue_count,
    source.informationalIssueCount,
    0,
  ),
),

maxDepth: asNumber(
  firstDefined(source.max_depth, source.maxDepth, source.tree_depth),
),



    updatedAt: firstDefined(source.updated_at, raw.updated_at, null),
    treeVersion: firstDefined(
      source.tree_version,
      source.version,
      raw.tree_version,
      null,
    ),
  };
}

function normalizeMeta(result, fallbackCount = 0) {
  const meta = result?.meta || unwrapResult(result)?.meta || {};
  const cursor = firstDefined(meta.next_cursor, meta.nextCursor, null);
  return {
    nextCursor: cursor,
    hasMore: asBoolean(
      firstDefined(meta.has_more, meta.hasMore, Boolean(cursor)),
      Boolean(cursor),
    ),
    total: asNumber(firstDefined(meta.total, meta.count, fallbackCount)),
  };
}

function createRequestId(prefix) {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return `${prefix}-${window.crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function Icon({ name, size = 18, strokeWidth = 1.9 }) {
  const paths = {
    tree: (
      <>
        <circle cx="12" cy="5" r="2.4" />
        <circle cx="5" cy="19" r="2.4" />
        <circle cx="19" cy="19" r="2.4" />
        <path d="M12 7.4v4.1M5 16.6v-2.2a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v2.2" />
      </>
    ),
    search: (
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-4-4" />
      </>
    ),
    refresh: (
      <>
        <path d="M20 7v5h-5" />
        <path d="M4.9 17a8 8 0 0 0 13.8-3M19.1 7A8 8 0 0 0 5.3 10M4 12V7h5" />
      </>
    ),
    health: (
      <>
        <path d="M12 21s8-3.6 8-10V5l-8-3-8 3v6c0 6.4 8 10 8 10Z" />
        <path d="m8.8 11.7 2.1 2.1 4.5-4.6" />
      </>
    ),
    history: (
      <>
        <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
        <path d="M3 3v5h5M12 7v5l3 2" />
      </>
    ),
    chevron: <path d="m9 18 6-6-6-6" />,
    users: (
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    move: (
      <>
        <path d="M5 9 2 12l3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3" />
        <path d="M2 12h20M12 2v20" />
      </>
    ),
    grip: (
      <>
        <circle cx="8" cy="7" r="1" />
        <circle cx="16" cy="7" r="1" />
        <circle cx="8" cy="12" r="1" />
        <circle cx="16" cy="12" r="1" />
        <circle cx="8" cy="17" r="1" />
        <circle cx="16" cy="17" r="1" />
      </>
    ),
    close: (
      <>
        <path d="m6 6 12 12M18 6 6 18" />
      </>
    ),
    check: <path d="m5 12 4 4L19 6" />,
    alert: (
      <>
        <path d="M10.3 3.6 1.9 18a2 2 0 0 0 1.7 3h16.8a2 2 0 0 0 1.7-3L13.7 3.6a2 2 0 0 0-3.4 0Z" />
        <path d="M12 9v4M12 17h.01" />
      </>
    ),
    unlink: (
      <>
        <path d="m9.5 14.5-2 2a3.5 3.5 0 0 1-5-5l3-3a3.5 3.5 0 0 1 5 0" />
        <path d="m14.5 9.5 2-2a3.5 3.5 0 0 1 5 5l-3 3a3.5 3.5 0 0 1-5 0M8 12h8" />
      </>
    ),
    cycle: (
      <>
        <path d="M17.7 6.3A8 8 0 0 0 4 12H1l4 4 4-4H6a6 6 0 0 1 10.2-4.2M6.3 17.7A8 8 0 0 0 20 12h3l-4-4-4 4h3a6 6 0 0 1-10.2 4.2" />
      </>
    ),
    code: (
      <>
        <path d="m8 9-3 3 3 3M16 9l3 3-3 3M14 5l-4 14" />
      </>
    ),
    userX: (
      <>
        <path d="M15 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M8 11a4 4 0 1 0 0-8M18 8l5 5M23 8l-5 5" />
      </>
    ),
    wallet: (
      <>
        <path d="M3 6h15a2 2 0 0 1 2 2v11H5a2 2 0 0 1-2-2V6Z" />
        <path d="M3 6a3 3 0 0 1 3-3h11v3M15 12h8v5h-8a2.5 2.5 0 0 1 0-5Z" />
      </>
    ),
    copy: (
      <>
        <rect x="9" y="9" width="11" height="11" rx="2" />
        <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
      </>
    ),
    arrow: (
      <>
        <path d="M5 12h14M13 6l6 6-6 6" />
      </>
    ),
    lock: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    ),
    eye: (
      <>
        <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
        <circle cx="12" cy="12" r="2.5" />
      </>
    ),


    trash: (
      <>
        <path d="M3 6h18" />
        <path d="M8 6V4h8v2" />
        <path d="M19 6l-1 15H6L5 6" />
        <path d="M10 11v6M14 11v6" />
      </>
    ),

    undo: (
      <>
        <path d="m9 7-5 5 5 5" />
        <path d="M20 17a7 7 0 0 0-7-7H4" />
      </>
    ),

  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths.alert}
    </svg>
  );
}

function StatusPill({ status, children }) {
  const normalized = String(status || "").toLowerCase();
  const tone = ["active", "healthy", "rewarded", "completed"].includes(
    normalized,
  )
    ? "success"
    : ["blocked", "frozen", "cycle", "broken", "failed"].includes(normalized)
      ? "danger"
      : ["pending", "warning", "inactive", "orphan"].includes(normalized)
        ? "warning"
        : "neutral";
  return (
    <span className={`mpat-pill is-${tone}`}>{children || status || "—"}</span>
  );
}

function Avatar({ node, size = "normal" }) {
  return (
    <span className={cx("mpat-avatar", size === "large" && "is-large")}>
      {getInitials(node?.name)}
    </span>
  );
}

function LoadingBlock({ text = "Veriler hazırlanıyor..." }) {
  return (
    <div className="mpat-loading" role="status">
      <span className="mpat-spinner" />
      <span>{text}</span>
    </div>
  );
}

function EmptyState({ icon = "tree", title, description, action }) {
  return (
    <div className="mpat-empty">
      <span>
        <Icon name={icon} size={24} />
      </span>
      <strong>{title}</strong>
      {description && <p>{description}</p>}
      {action}
    </div>
  );
}

function StatCard({ icon, label, value, hint, tone = "blue", onClick }) {
  const Component = onClick ? "button" : "div";
  return (
    <Component
      type={onClick ? "button" : undefined}
      className={cx("mpat-stat", `is-${tone}`, onClick && "is-clickable")}
      onClick={onClick}
    >
      <span className="mpat-stat-icon">
        <Icon name={icon} size={20} />
      </span>
      <span className="mpat-stat-copy">
        <span>{label}</span>
        <strong>{formatNumber(value)}</strong>
        {hint && <small>{hint}</small>}
      </span>
    </Component>
  );
}

function UserSummary({ node, compact = false }) {
  if (!node) return null;
  return (
    <div className={cx("mpat-user-summary", compact && "is-compact")}>
      <Avatar node={node} size={compact ? "normal" : "large"} />
      <div>
        <strong>{node.name}</strong>
        <span>{node.email || "E-posta bilgisi yok"}</span>
        <small>Kullanıcı ID: {node.id}</small>
      </div>
    </div>
  );
}

function Modal({ title, description, onClose, children, size = "normal" }) {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div className="mpat-modal-backdrop" role="presentation">
      <section
        className={cx("mpat-modal", size === "large" && "is-large")}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <div>
            <h3>{title}</h3>
            {description && <p>{description}</p>}
          </div>
          <button type="button" onClick={onClose} aria-label="Kapat">
            <Icon name="close" />
          </button>
        </header>
        <div className="mpat-modal-body">{children}</div>
      </section>
    </div>
  );
}

export default function MespuanAgacYonetimi({
  authToken = "",
  apiBase = DEFAULT_API_BASE,
  endpoints: endpointOverrides = {},
  currentUser = null,
  request = null,
  onUnauthorized = null,
}) {
  const endpoints = useMemo(
    () => ({ ...DEFAULT_ENDPOINTS, ...endpointOverrides }),
    [endpointOverrides],
  );
  const token = useMemo(
    () => normalizeToken(authToken) || getStoredToken(),
    [authToken],
  );
  const tokenPayload = useMemo(() => decodeJwt(token), [token]);
  const signedInUser = currentUser || tokenPayload || {};
  const signedInEmail = String(
    firstDefined(
      signedInUser.email,
      signedInUser.mail,
      signedInUser.user_email,
      signedInUser.eposta,
      "",
    ),
  )
    .trim()
    .toLowerCase();
  const canAccess = signedInEmail === MANAGER_EMAIL;

  const [view, setView] = useState("tree");
  const [summary, setSummary] = useState(normalizeSummary({}));
  const [nodeStore, setNodeStore] = useState({});
  const [rootIds, setRootIds] = useState([]);
  const [childIds, setChildIds] = useState({});
  const [branchMeta, setBranchMeta] = useState({});
  const [expanded, setExpanded] = useState(() => new Set());
  const [loadingBranches, setLoadingBranches] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [pageError, setPageError] = useState("");
  const [toast, setToast] = useState(null);
  const [rootFilter, setRootFilter] = useState("all");
  const [compactTree, setCompactTree] = useState(false);

  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

const searchSequence = useRef(0);
const sponsorSearchRef = useRef(null);


  const [dragSourceId, setDragSourceId] = useState(null);
  const [dragTargetId, setDragTargetId] = useState(null);
  const [moveSourceId, setMoveSourceId] = useState(null);
  const [moveDialog, setMoveDialog] = useState(null);

  const [healthLoading, setHealthLoading] = useState(false);
  const [healthIssues, setHealthIssues] = useState([]);
  const [healthFilter, setHealthFilter] = useState("all");
  const [healthMeta, setHealthMeta] = useState({});

  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyMeta, setHistoryMeta] = useState({});
  const [undoDialog, setUndoDialog] = useState(null);

  // Tüm ekibi tek ekranda gösterme
  const [teamDialog, setTeamDialog] = useState(null);

  // Güvenli kullanıcı silme
  const [deleteDialog, setDeleteDialog] = useState(null);

  // Tek kullanımlık cihaz erişimi sıfırlama
  const [
    deviceResetEmail,
    setDeviceResetEmail,
  ] = useState("");

  const [
    deviceResetBusy,
    setDeviceResetBusy,
  ] = useState(false);

  const [
    deviceResetError,
    setDeviceResetError,
  ] = useState("");


const [
  deviceResetResult,
  setDeviceResetResult,
] = useState(null);

// E-posta ile tam hesap / MesPuan temizliği
const [purgeEmail, setPurgeEmail] = useState("");
const [purgeConfirmation, setPurgeConfirmation] = useState("");
const [purgeBusy, setPurgeBusy] = useState(false);
const [purgeError, setPurgeError] = useState("");
const [purgeResult, setPurgeResult] = useState(null);

const notify = useCallback((message, type = "success") => {


    setToast({ message, type });
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = window.setTimeout(() => setToast(null), 4500);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const callApi = useCallback(
    async (path, options = {}) => {
      if (request) {
        return request(path, options);
      }
      if (!token) {
        const error = new Error(
          "Oturum bilgisi bulunamadı. Yeniden giriş yapın.",
        );
        error.status = 401;
        throw error;
      }

      const response = await fetch(
        `${String(apiBase).replace(/\/$/, "")}${path}`,
        {
          method: options.method || "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json; charset=UTF-8",
            Authorization: `Bearer ${token}`,
            ...(options.headers || {}),
          },
          body:
            options.body === undefined
              ? undefined
              : JSON.stringify(options.body),
          signal: options.signal,
        },
      );

      if (response.status === 204) return { success: true, data: null };

      let result;
      try {
        result = await response.json();
      } catch (_) {
        const error = new Error("Sunucudan geçerli bir yanıt alınamadı.");
        error.status = response.status;
        throw error;
      }

      if (!response.ok || result?.success === false) {
        const error = new Error(
          result?.message || "Referans ağacı işlemi tamamlanamadı.",
        );
        error.code = result?.code;
        error.status = response.status;
        error.details = result?.details;
        if (response.status === 401 && onUnauthorized) onUnauthorized(error);
        throw error;
      }
      return result;
    },
    [apiBase, onUnauthorized, request, token],
  );

  const mergeNodes = useCallback((rawItems) => {
    const normalized = rawItems.map(normalizeNode).filter(Boolean);
    setNodeStore((current) => {
      const next = { ...current };
      normalized.forEach((node) => {
        next[node.id] = {
          ...(current[node.id] || {}),
          ...node,
          wallet: {
            ...(current[node.id]?.wallet || {}),
            ...node.wallet,
          },
        };
      });
      return next;
    });
    return normalized;
  }, []);

  const loadSummary = useCallback(async () => {



    const result = await callApi(endpoints.summary);
    setSummary(normalizeSummary(result));
  }, [callApi, endpoints.summary]);

  const loadRoots = useCallback(
    async ({ append = false } = {}) => {
      setTreeLoading(true);
      try {
        const cursor = append ? branchMeta.root?.nextCursor : null;
        const result = await callApi(
          `${endpoints.roots}${makeQuery({
            status: rootFilter === "all" ? "" : rootFilter,
            cursor,
            limit: PAGE_SIZE,
          })}`,
        );
        const collection = extractCollection(result, ["roots"]);
        const nodes = mergeNodes(collection.items);
        const ids = nodes.map((node) => node.id);
        setRootIds((current) =>
          append ? [...new Set([...current, ...ids])] : ids,
        );
        setBranchMeta((current) => ({
          ...current,
          root: normalizeMeta(result, ids.length),
        }));
      } catch (error) {
        setPageError(error.message);
      } finally {
        setTreeLoading(false);
      }
    },
    [
      branchMeta.root?.nextCursor,
      callApi,
      endpoints.roots,
      mergeNodes,
      rootFilter,
    ],
  );

  const loadChildren = useCallback(
    async (userId, { append = false, open = true } = {}) => {
      const id = asPositiveInt(userId);
      if (!id || loadingBranches.has(id)) return;

      setLoadingBranches((current) => new Set([...current, id]));
      try {
        const cursor = append ? branchMeta[id]?.nextCursor : null;
        const result = await callApi(
          `${endpoints.children(id)}${makeQuery({ cursor, limit: PAGE_SIZE })}`,
        );
        const collection = extractCollection(result, ["children"]);
        const nodes = mergeNodes(collection.items);
        const ids = nodes.map((node) => node.id);
        setChildIds((current) => ({
          ...current,
          [id]: append ? [...new Set([...(current[id] || []), ...ids])] : ids,
        }));
        setBranchMeta((current) => ({
          ...current,
          [id]: normalizeMeta(result, ids.length),
        }));
        if (open) setExpanded((current) => new Set([...current, id]));
      } catch (error) {
        notify(error.message, "error");
      } finally {
        setLoadingBranches((current) => {
          const next = new Set(current);
          next.delete(id);
          return next;
        });
      }
    },
    [branchMeta, callApi, endpoints, loadingBranches, mergeNodes, notify],
  );

  const toggleNode = useCallback(
    async (node) => {
      if (!node?.id) return;
      if (expanded.has(node.id)) {
        setExpanded((current) => {
          const next = new Set(current);
          next.delete(node.id);
          return next;
        });
        return;
      }
      if (childIds[node.id]) {
        setExpanded((current) => new Set([...current, node.id]));
        return;
      }
      await loadChildren(node.id);
    },
    [childIds, expanded, loadChildren],
  );

  const selectUser = useCallback(
    async (userId, { keepView = false } = {}) => {
      const id = asPositiveInt(userId);
      if (!id) return;
      setSelectedId(id);
      setSearchOpen(false);
      if (!keepView) setView("tree");
      setSelectedLoading(true);
      try {
        const result = await callApi(endpoints.details(id));
        const raw = unwrapResult(result);
        const node = normalizeNode(raw?.user || raw?.item || raw);
        if (node) mergeNodes([node]);
      } catch (error) {
        notify(error.message, "error");
      } finally {
        setSelectedLoading(false);
      }
    },
    [callApi, endpoints, mergeNodes, notify],
  );

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      const result = await callApi(
        `${endpoints.health}${makeQuery({
          type: healthFilter === "all" ? "" : healthFilter,
          limit: PAGE_SIZE,
        })}`,
      );
      const data = unwrapResult(result) || {};
      const collection = extractCollection(result, ["issues", "problems"]);
      setHealthIssues(collection.items);
      setHealthMeta({
        ...normalizeMeta(result, collection.items.length),
        counts: data.counts || data.issue_counts || {},
      });
    } catch (error) {
      setPageError(error.message);
    } finally {
      setHealthLoading(false);
    }
  }, [callApi, endpoints.health, healthFilter]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const result = await callApi(
        `${endpoints.history}${makeQuery({ limit: 50 })}`,
      );
      const collection = extractCollection(result, ["history", "moves"]);
      setHistoryItems(collection.items);
      setHistoryMeta(normalizeMeta(result, collection.items.length));
    } catch (error) {
      setPageError(error.message);
    } finally {
      setHistoryLoading(false);
    }
  }, [callApi, endpoints.history]);

  const refreshAll = useCallback(async () => {
    setPageError("");
    setExpanded(new Set());
    setChildIds({});
    setBranchMeta({});
    await Promise.all([loadSummary(), loadRoots()]);
    if (view === "health") await loadHealth();
    if (view === "history") await loadHistory();
  }, [loadHealth, loadHistory, loadRoots, loadSummary, view]);

  useEffect(() => {
    if (!canAccess) return;
    setPageError("");
    Promise.all([loadSummary(), loadRoots()]).catch((error) =>
      setPageError(error.message),
    );
  }, [canAccess, rootFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canAccess || view !== "health") return;
    loadHealth();
  }, [canAccess, healthFilter, view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canAccess || view !== "history") return;
    loadHistory();
  }, [canAccess, view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const q = searchText.trim();
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    const sequence = ++searchSequence.current;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await callApi(
          `${endpoints.search}${makeQuery({ q, limit: 30 })}`,
          { signal: controller.signal },
        );
        if (sequence !== searchSequence.current) return;
        const collection = extractCollection(result, ["results"]);
        const nodes = mergeNodes(collection.items);
        setSearchResults(nodes.map((node) => node.id));
        setSearchOpen(true);
      } catch (error) {
        if (
          error.name !== "AbortError" &&
          sequence === searchSequence.current
        ) {
          notify(error.message, "error");
        }
      } finally {
        if (sequence === searchSequence.current) setSearchLoading(false);
      }
    }, 350);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [callApi, endpoints.search, mergeNodes, notify, searchText]);


  const selectedNode = selectedId ? nodeStore[selectedId] : null;
  const moveSourceNode = moveSourceId ? nodeStore[moveSourceId] : null;


  // ============================================================
  // TÜM EKİBİ TEK EKRANDA GÖR
  // ============================================================

  const openTeam = useCallback(
    async (userId) => {
      const id = asPositiveInt(userId);
      const owner = id ? nodeStore[id] : null;

      if (!id || !owner) return;

      setTeamDialog({
        owner,
        loading: true,
        members: [],
        error: "",
      });

      try {
        const result = await callApi(endpoints.team(id));
        const data = unwrapResult(result) || {};

        const rawMembers = Array.isArray(data.members)
          ? data.members
          : Array.isArray(data.team)
            ? data.team
            : [];

        const members = rawMembers
          .map((raw) => {
            const node = normalizeNode(raw);

            if (!node) return null;

            return {
              ...node,

              teamLevel: asNumber(
                firstDefined(
                  raw.team_level,
                  raw.teamLevel,
                  raw.relative_level,
                ),
                0,
              ),

              parentUserId: asPositiveInt(
                firstDefined(
                  raw.parent_user_id,
                  raw.parentUserId,
                ),
              ),
            };
          })
          .filter(Boolean);

        mergeNodes(rawMembers);

        setTeamDialog({
          owner,
          loading: false,
          members,
          error: "",
        });
      } catch (error) {
        setTeamDialog({
          owner,
          loading: false,
          members: [],
          error: error.message,
        });
      }
    },
    [callApi, endpoints, mergeNodes, nodeStore],
  );


  // ============================================================
  // KULLANICIYI SİL
  // ============================================================

  const openDeleteUser = useCallback(
    (userId) => {
      const id = asPositiveInt(userId);
      const node = id ? nodeStore[id] : null;

      if (!node) return;

      setDeleteDialog({
        node,
        submitting: false,
        error: "",
      });
    },
    [nodeStore],
  );


  const commitDeleteUser = useCallback(async () => {
    if (!deleteDialog?.node) return;

    const node = deleteDialog.node;

    setDeleteDialog((current) =>
      current
        ? {
            ...current,
            submitting: true,
            error: "",
          }
        : current,
    );

    try {



    const result = await callApi(
  endpoints.purgeByEmail,
  {
    method: "POST",

    body: {
      email: node.email,
      confirmation: "TAM SİL",
    },
  },
);




      const data = unwrapResult(result) || {};

      setDeleteDialog(null);
      setTeamDialog(null);
      setSelectedId(null);

      setMoveSourceId((current) =>
        Number(current) === Number(node.id)
          ? null
          : current,
      );

      setNodeStore((current) => {
        const next = { ...current };
        delete next[node.id];
        return next;
      });

      notify(
        data.message ||
          `${node.name} kalıcı olarak silindi.`,
      );

      await refreshAll();
    } catch (error) {
      setDeleteDialog((current) =>
        current
          ? {
              ...current,
              submitting: false,
              error: error.message,
            }
          : current,
      );
    }



  }, [
    callApi,
    deleteDialog,
    endpoints,
    notify,
    refreshAll,
  ]);


  // ============================================================
  // CİHAZ ERİŞİMİNİ TEK KULLANIMLIK SIFIRLA
  // ============================================================

  const submitDeviceReset =
    useCallback(
      async (event) => {

        event?.preventDefault();

        const email =
          String(
            deviceResetEmail || ""
          )
            .trim()
            .toLowerCase();

        if (
          !email ||
          !email.includes("@")
        ) {
          setDeviceResetError(
            "Geçerli bir kullanıcı e-postası yazın.",
          );
          return;
        }

        setDeviceResetBusy(true);
        setDeviceResetError("");
        setDeviceResetResult(null);

        try {

          const result =
            await callApi(
              endpoints.deviceReset,
              {
                method: "POST",
                body: {
                  email,
                },
              },
            );

          const data =
            unwrapResult(result) || {};

          setDeviceResetResult(
            data,
          );

          setDeviceResetEmail("");

          notify(
            data.message ||
              `${email} için cihaz erişimi sıfırlandı.`,
          );

        } catch (error) {

          setDeviceResetError(
            error.message ||
              "Cihaz erişimi sıfırlanamadı.",
          );

        } finally {

          setDeviceResetBusy(false);

        }
      },
      [
        callApi,
        deviceResetEmail,
        endpoints.deviceReset,
        notify,
      ],
    );



// ============================================================
// E-POSTA İLE TAM HESAP / MESPUAN TEMİZLİĞİ
// ============================================================

const submitEmailPurge =
  useCallback(
    async (event) => {

      event?.preventDefault();

      const email = String(
        purgeEmail || ""
      )
        .trim()
        .toLowerCase();

      const confirmation = String(
        purgeConfirmation || ""
      )
        .trim()
        .toLocaleUpperCase("tr-TR");

      if (
        !email ||
        !email.includes("@")
      ) {
        setPurgeError(
          "Geçerli bir kullanıcı e-postası yazın.",
        );
        return;
      }

      if (
        confirmation !== "TAM SİL"
      ) {
        setPurgeError(
          "Onay alanına TAM SİL yazın.",
        );
        return;
      }

      setPurgeBusy(true);
      setPurgeError("");
      setPurgeResult(null);

      try {

        const result =
          await callApi(
            endpoints.purgeByEmail,
            {
              method: "POST",

              body: {
                email,
                confirmation: "TAM SİL",
              },
            },
          );

        const data =
          unwrapResult(result) || {};

        setPurgeResult(data);
        setPurgeEmail("");
        setPurgeConfirmation("");

        notify(
          data.message ||
            `${email} ile ilişkili kayıtlar tamamen temizlendi.`,
        );

        await refreshAll();

      } catch (error) {

        setPurgeError(
          error.message ||
            "E-posta ile tam temizlik tamamlanamadı.",
        );

      } finally {

        setPurgeBusy(false);

      }
    },
    [
      callApi,
      endpoints.purgeByEmail,
      notify,
      purgeConfirmation,
      purgeEmail,
      refreshAll,
    ],
  );

  const beginMove = useCallback(



    (userId) => {
      const node = nodeStore[userId];
      if (!node) return;

      setMoveSourceId(node.id);

      notify(
        `${node.name} ve altındaki ${formatNumber(
          node.teamCount,
        )} kişi için yeni sponsor seçin.`,
        "info",
      );
    },
    [nodeStore, notify],
  );

  // ----------------------------------------------------------------------
  // KULLANICIYI SEÇ -> SPONSOR ARA -> EKİBİYLE TAŞI
  // ----------------------------------------------------------------------
  const beginMoveWithSearch = useCallback(
    (userId) => {
      const node = nodeStore[userId];
      if (!node) return;

      setMoveSourceId(node.id);

      // Önceki genel aramayı temizle.
      setSearchText("");
      setSearchResults([]);
      setSearchOpen(false);

      notify(
        `${node.name} ve altındaki ${formatNumber(
          node.teamCount,
        )} kişi taşınacak. Yeni sponsoru ad, e-posta, telefon veya kullanıcı ID ile arayın.`,
        "info",
      );

      // Mevcut global arama kutusuna otomatik götür.
      window.setTimeout(() => {
        if (!sponsorSearchRef.current) return;

        sponsorSearchRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        sponsorSearchRef.current.focus();
      }, 100);
    },
    [nodeStore, notify],
  );

  // ----------------------------------------------------------------------
  // GÜVENLİ TAŞIMA ÖN İZLEMESİ
  // ----------------------------------------------------------------------
  const previewMove = useCallback(
    async (sourceId, targetId) => {
      const source = nodeStore[sourceId];
      const target = nodeStore[targetId];

      if (!source || !target) return;

      if (source.id === target.id) {
        notify("Bir kullanıcı kendi altına taşınamaz.", "error");
        return;
      }

      if (source.sponsorId === target.id) {
        notify(`${source.name} zaten ${target.name} altında.`, "info");
        return;
      }

      setMoveDialog({
        source,
        target,
        loading: true,
        preview: null,
        reason: "",
        confirmation: "",
        error: "",
        submitting: false,
      });



    const requestPreview = async (treeVersion) => {
      return callApi(endpoints.movePreview, {
        method: "POST",
        body: {
          source_user_id: source.id,
          target_sponsor_user_id: target.id,
          mode: "subtree",
          requested_tree_version: treeVersion,
        },
      });
    };

    try {
      const result = await requestPreview(summary.treeVersion);
      const preview = unwrapResult(result) || {};

      setMoveDialog((current) =>
        current
          ? {
              ...current,
              loading: false,
              preview,
              error: "",
            }
          : current,
      );
    } catch (error) {
      if (error?.code === "TREE_VERSION_CHANGED") {
        try {
          const summaryResult = await callApi(endpoints.summary);
          const freshSummary = normalizeSummary(summaryResult);

          setSummary(freshSummary);

          const retryResult = await requestPreview(
            freshSummary.treeVersion,
          );
          const freshPreview = unwrapResult(retryResult) || {};

          setMoveDialog((current) =>
            current
              ? {
                  ...current,
                  loading: false,
                  preview: freshPreview,
                  error: "",
                }
              : current,
          );

          notify(
            "Referans ağacı güncellendi. Taşıma ön izlemesi otomatik olarak yenilendi.",
            "info",
          );

          return;
        } catch (retryError) {
          setMoveDialog((current) =>
            current
              ? {
                  ...current,
                  loading: false,
                  preview: null,
                  error: retryError.message,
                }
              : current,
          );

          return;
        }
      }

      setMoveDialog((current) =>
        current
          ? {
              ...current,
              loading: false,
              preview: null,
              error: error.message,
            }
          : current,
      );
    }
  },
  [
    callApi,
    endpoints.movePreview,
    endpoints.summary,
    nodeStore,
    notify,
    summary.treeVersion,
  ],
);




  const commitMove = useCallback(async () => {
    if (!moveDialog?.preview) return;
    const reason = moveDialog.reason.trim();
    if (reason.length < 15) {
      setMoveDialog((current) => ({
        ...current,
        error: "İşlem gerekçesi en az 15 karakter olmalıdır.",
      }));
      return;
    }
    if (moveDialog.confirmation.trim().toLocaleUpperCase("tr-TR") !== "TAŞI") {
      setMoveDialog((current) => ({
        ...current,
        error: "Onay alanına TAŞI yazın.",
      }));
      return;
    }

    setMoveDialog((current) => ({ ...current, submitting: true, error: "" }));
    try {
      const preview = moveDialog.preview;
      const result = await callApi(endpoints.moveCommit, {
        method: "POST",
        headers: {
          "Idempotency-Key": createRequestId("referral-tree-move"),
        },
        body: {
          preview_token: firstDefined(
            preview.preview_token,
            preview.token,
            preview.operation_token,
          ),
          source_user_id: moveDialog.source.id,
          target_sponsor_user_id: moveDialog.target.id,
          mode: "subtree",
          reason,
          expected_tree_version: firstDefined(
            preview.tree_version,
            preview.expected_tree_version,
            summary.treeVersion,
          ),
        },
      });
      const data = unwrapResult(result) || {};
      setMoveDialog(null);
      setMoveSourceId(null);
      setDragSourceId(null);
      setDragTargetId(null);
      notify(
        data.message ||
          `${moveDialog.source.name} ve ekibi ${moveDialog.target.name} altına güvenle taşındı.`,
      );
      await refreshAll();
      await selectUser(moveDialog.source.id);
    } catch (error) {
      setMoveDialog((current) =>
        current
          ? { ...current, submitting: false, error: error.message }
          : current,
      );
    }
  }, [
    callApi,
    endpoints.moveCommit,
    moveDialog,
    notify,
    refreshAll,
    selectUser,
    summary.treeVersion,
  ]);

  const openUndo = useCallback(
    async (historyItem) => {
      const moveId = asPositiveInt(
        firstDefined(historyItem.id, historyItem.move_id, historyItem.audit_id),
      );
      if (!moveId) return;
      setUndoDialog({
        item: historyItem,
        moveId,
        loading: true,
        preview: null,
        reason: "",
        confirmation: "",
        error: "",
        submitting: false,
      });
      try {
        const result = await callApi(endpoints.undoPreview(moveId), {
          method: "POST",
          body: { requested_tree_version: summary.treeVersion },
        });
        setUndoDialog((current) =>
          current
            ? {
                ...current,
                loading: false,
                preview: unwrapResult(result) || {},
              }
            : current,
        );
      } catch (error) {
        setUndoDialog((current) =>
          current
            ? { ...current, loading: false, error: error.message }
            : current,
        );
      }
    },
    [callApi, endpoints, summary.treeVersion],
  );

  const commitUndo = useCallback(async () => {
    if (!undoDialog?.preview) return;
    const reason = undoDialog.reason.trim();
    if (reason.length < 15) {
      setUndoDialog((current) => ({
        ...current,
        error: "Geri alma gerekçesi en az 15 karakter olmalıdır.",
      }));
      return;
    }
    if (
      undoDialog.confirmation.trim().toLocaleUpperCase("tr-TR") !== "GERİ AL"
    ) {
      setUndoDialog((current) => ({
        ...current,
        error: "Onay alanına GERİ AL yazın.",
      }));
      return;
    }

    setUndoDialog((current) => ({ ...current, submitting: true, error: "" }));
    try {
      const preview = undoDialog.preview;
      await callApi(endpoints.undoCommit(undoDialog.moveId), {
        method: "POST",
        headers: {
          "Idempotency-Key": createRequestId("referral-tree-undo"),
        },
        body: {
          preview_token: firstDefined(preview.preview_token, preview.token),
          reason,
          expected_tree_version: firstDefined(
            preview.tree_version,
            preview.expected_tree_version,
            summary.treeVersion,
          ),
        },
      });
      setUndoDialog(null);
      notify("Taşıma işlemi güvenli biçimde geri alındı.");
      await refreshAll();
      await loadHistory();
    } catch (error) {
      setUndoDialog((current) =>
        current
          ? { ...current, submitting: false, error: error.message }
          : current,
      );
    }
  }, [
    callApi,
    endpoints,
    loadHistory,
    notify,
    refreshAll,
    summary.treeVersion,
    undoDialog,
  ]);

  const copyText = useCallback(
    async (value, label) => {
      try {
        await navigator.clipboard.writeText(String(value || ""));
        notify(`${label} kopyalandı.`);
      } catch (_) {
        notify("Kopyalama işlemi yapılamadı.", "error");
      }
    },
    [notify],
  );

  const TreeNode = useCallback(
    function TreeNodeComponent({ nodeId, depth = 0 }) {
      const node = nodeStore[nodeId];
      if (!node) return null;
      const isOpen = expanded.has(node.id);
      const isLoading = loadingBranches.has(node.id);
      const children = childIds[node.id] || [];
      const mayHaveChildren = node.directCount > 0 || children.length > 0;
      const isSelected = selectedId === node.id;
      const isMoveSource = moveSourceId === node.id;
      const isDropTarget = dragTargetId === node.id;
      const cannotTarget =
        moveSourceId === node.id || node.sponsorId === moveSourceId;

      return (
        <div
          className="mpat-tree-item"
          role="treeitem"
          aria-expanded={mayHaveChildren ? isOpen : undefined}
        >
          <div
            className={cx(
              "mpat-node",
              compactTree && "is-compact",
              isSelected && "is-selected",
              isMoveSource && "is-move-source",
              isDropTarget && "is-drop-target",
            )}
            style={{ "--mpat-depth": Math.min(depth, 12) }}
            draggable={!moveDialog && !undoDialog}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", String(node.id));
              setDragSourceId(node.id);
              setMoveSourceId(node.id);
            }}
            onDragEnd={() => {
              setDragSourceId(null);
              setDragTargetId(null);
            }}
            onDragOver={(event) => {
              if (!dragSourceId || dragSourceId === node.id) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              setDragTargetId(node.id);
            }}
            onDragLeave={() => {
              if (dragTargetId === node.id) setDragTargetId(null);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const sourceId = asPositiveInt(
                event.dataTransfer.getData("text/plain") || dragSourceId,
              );
              setDragTargetId(null);
              if (sourceId) previewMove(sourceId, node.id);
            }}
          >
            <span className="mpat-branch-line" aria-hidden="true" />
            <button
              type="button"
              className="mpat-expand"
              disabled={!mayHaveChildren || isLoading}
              onClick={() => toggleNode(node)}
              aria-label={isOpen ? "Ekibi kapat" : "Ekibi aç"}
            >
              {isLoading ? (
                <span className="mpat-spinner is-small" />
              ) : (
                <Icon name="chevron" size={16} />
              )}
            </button>
            <button
              type="button"
              className="mpat-grip"
              onClick={() => beginMove(node.id)}
              aria-label={`${node.name} için taşıma başlat`}
              title="Tutun ve yeni sponsor üzerine bırakın"
            >
              <Icon name="grip" size={18} />
            </button>
            <button
              type="button"
              className="mpat-node-main"
              onClick={() => {
                selectUser(node.id);
                if (mayHaveChildren && !isOpen) toggleNode(node);
              }}
            >
              <Avatar node={node} />
              <span className="mpat-node-identity">
                <strong>{node.name}</strong>
                <span>{node.email || `Kullanıcı #${node.id}`}</span>
              </span>
            </button>
            <span className="mpat-node-counts">
              <span title="Doğrudan bağlı kişi">
                <b>{formatNumber(node.directCount)}</b> direkt
              </span>
              <span title="Toplam alt ekip">
                <b>{formatNumber(node.teamCount)}</b> ekip
              </span>
            </span>
            <span className="mpat-node-status">
              <StatusPill status={node.active ? "active" : "inactive"}>
                {node.active ? "Aktif" : "Pasif"}
              </StatusPill>
            </span>
            {moveSourceId && !isMoveSource && (
              <button
                type="button"
                className="mpat-target-button"
                disabled={cannotTarget}
                onClick={() => previewMove(moveSourceId, node.id)}
              >
                Buraya taşı
              </button>
            )}
          </div>

          {isOpen && (
            <div className="mpat-tree-children" role="group">
              {children.map((childId) => (
                <TreeNodeComponent
                  key={childId}
                  nodeId={childId}
                  depth={depth + 1}
                />
              ))}
              {!isLoading && children.length === 0 && (
                <div
                  className="mpat-branch-empty"
                  style={{ "--mpat-depth": Math.min(depth + 1, 12) }}
                >
                  Bu kullanıcının altında kayıtlı kişi bulunmuyor.
                </div>
              )}
              {branchMeta[node.id]?.hasMore && (
                <button
                  type="button"
                  className="mpat-load-more is-branch"
                  onClick={() => loadChildren(node.id, { append: true })}
                >
                  Alt ekipten daha fazla yükle
                </button>
              )}
            </div>
          )}
        </div>
      );
    },
    [
      beginMove,
      branchMeta,
      childIds,
      compactTree,
      dragSourceId,
      dragTargetId,
      expanded,
      loadChildren,
      loadingBranches,
      moveDialog,
      moveSourceId,
      nodeStore,
      previewMove,
      selectUser,
      selectedId,
      toggleNode,
      undoDialog,
    ],
  );

  if (!token) {
    return (
      <div className="mpat-shell">
        <Style />
        <EmptyState
          icon="lock"
          title="Oturum bilgisi bulunamadı"
          description="Referans ağacı yönetimi için MesYat Admin hesabıyla yeniden giriş yapın."
        />
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="mpat-shell">
        <Style />
        <EmptyState
          icon="lock"
          title="Bu bölüm yalnızca MesYat merkez yönetimine açıktır"
          description="Referans ağacını görüntüleme ve taşıma yetkisi yalnızca mesyat@admin.com hesabındadır."
        />
      </div>
    );
  }

  return (
    <div className="mpat-shell">
      <Style />

      <header className="mpat-hero">
        <div className="mpat-hero-copy">
          <span className="mpat-eyebrow">MESYAT DİJİTAL · MERKEZ YÖNETİMİ</span>
          <h1>MesPuan Referans Ağacı</h1>
          <p>
            Kullanıcıları, sponsor bağlantılarını ve tüm alt ekipleri tek
            merkezden güvenli biçimde inceleyin ve yönetin.
          </p>
        </div>
        <div className="mpat-hero-actions">
          <span className="mpat-readonly-badge">
            <Icon name="lock" size={16} /> MesPuan verileri salt okunur
          </span>
          <button
            type="button"
            className="mpat-btn is-light"
            onClick={refreshAll}
          >
            <Icon name="refresh" size={17} /> Verileri yenile
          </button>
        </div>
      </header>



<nav className="mpat-tabs" aria-label="Ağaç yönetimi bölümleri">
  <button
    type="button"
    className={view === "tree" ? "is-active" : ""}
    onClick={() => setView("tree")}
  >
    <Icon name="tree" /> Ağaç Görünümü
  </button>

  <button
    type="button"
    className={view === "health" ? "is-active" : ""}
    onClick={() => setView("health")}
  >
    <Icon name="health" /> Sağlık Kontrolü

    {summary.criticalIssueCount > 0 && (
      <b>{formatNumber(summary.criticalIssueCount)}</b>
    )}
  </button>

  <button
    type="button"
    className={view === "history" ? "is-active" : ""}
    onClick={() => setView("history")}
  >
    <Icon name="history" /> İşlem Geçmişi
  </button>

<button
  type="button"
  className={
    view === "deviceAccess"
      ? "is-active"
      : ""
  }
  onClick={() =>
    setView("deviceAccess")
  }
>
  <Icon name="lock" />
  Cihaz Erişimi
</button>
</nav>





      {pageError && (
        <div className="mpat-alert is-error">
          <Icon name="alert" />
          <div>
            <strong>Veriler alınamadı</strong>
            <span>{pageError}</span>
          </div>
          <button type="button" onClick={refreshAll}>
            Tekrar dene
          </button>
        </div>
      )}

      <section className="mpat-stats">
        <StatCard
          icon="users"
          label="Toplam kullanıcı"
          value={summary.totalUsers}
          tone="blue"
        />
        <StatCard
          icon="tree"
          label="Ağaçtaki kullanıcı"
          value={summary.treeUsers}
          tone="cyan"
        />
        <StatCard
          icon="user"
          label="Kök hesap"
          value={summary.rootUsers}
          tone="orange"
        />


 <StatCard
  icon="health"
  label="Kritik sorun"
  value={summary.criticalIssueCount}
  hint={`${formatNumber(summary.warningIssueCount)} uyarı`}
  tone={
    summary.criticalIssueCount > 0
      ? "red"
      : summary.warningIssueCount > 0
        ? "orange"
        : "green"
  }
  onClick={() => setView("health")}
/>



        <StatCard
          icon="tree"
          label="En yüksek derinlik"
          value={summary.maxDepth}
          hint="seviye"
          tone="purple"
        />
      </section>

      <section className="mpat-search-wrap">
        <div className="mpat-search">
          <Icon name="search" />
        
<input
  ref={sponsorSearchRef}
  value={searchText}
  onChange={(event) => setSearchText(event.target.value)}
  onFocus={() => searchResults.length && setSearchOpen(true)}
  placeholder={
    moveSourceId
      ? `${moveSourceNode?.name || "Kullanıcı"} için yeni sponsor ara...`
      : "Ad, e-posta, telefon veya kullanıcı ID ile tüm ağacı ara..."
  }
  aria-label={
    moveSourceId
      ? "Yeni sponsor ara"
      : "Tüm referans ağacında kullanıcı ara"
  }
/>

          {searchLoading && <span className="mpat-spinner is-small" />}
          {searchText && (
            <button
              type="button"
              aria-label="Aramayı temizle"
              onClick={() => {
                setSearchText("");
                setSearchOpen(false);
              }}
            >
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
        {searchOpen && searchText.trim().length >= 2 && (
          <div className="mpat-search-results">
            <div className="mpat-search-title">
              <span>Tüm sistem sonuçları</span>
              <b>{searchResults.length} kayıt</b>
            </div>
            {searchResults.length
              ? searchResults.map((id) => {
                  const node = nodeStore[id];
                  if (!node) return null;
                  return (
                   
<button
  type="button"
  key={id}
  disabled={Boolean(moveSourceId && Number(moveSourceId) === Number(node.id))}
  onClick={() => {
    // Taşıma modu açıksa arama sonucu yeni sponsor olur.
    if (moveSourceId) {
      if (Number(moveSourceId) === Number(node.id)) {
        notify("Bir kullanıcı kendi altına taşınamaz.", "error");
        return;
      }

      setSearchOpen(false);
      previewMove(moveSourceId, node.id);
      return;
    }

    // Normal aramada eski davranış aynen devam eder.
    selectUser(id);
  }}
>
  <Avatar node={node} />

  <span>
    <strong>{node.name}</strong>
    <small>{node.email || `ID: ${node.id}`}</small>
  </span>

  <span className="mpat-search-meta">
    {moveSourceId ? (
      Number(moveSourceId) === Number(node.id) ? (
        <b>Taşınacak kişi</b>
      ) : (
        <>
          <b>Yeni sponsor seç</b>
          <Icon name="move" size={16} />
        </>
      )
    ) : (
      <>
        <b>{formatNumber(node.teamCount)}</b> ekip
        <Icon name="chevron" size={16} />
      </>
    )}
  </span>
</button>


                  );
                })
              : !searchLoading && (
                  <p>Arama ölçütlerine uygun kullanıcı bulunamadı.</p>
                )}
          </div>
        )}
      </section>

      {moveSourceNode && !moveDialog && (
        <div className="mpat-move-banner">
          <span className="mpat-move-icon">
            <Icon name="move" />
          </span>
          <div>
            <strong>{moveSourceNode.name} ve ekibi taşınacak</strong>
            <span>
              Yeni sponsorun kartına bırakın veya kart üzerindeki “Buraya taşı”
              düğmesini kullanın. İşlem doğrudan yapılmaz; önce ayrıntılı ön
              izleme açılır.
            </span>
          </div>
          <button type="button" onClick={() => setMoveSourceId(null)}>
            İptal
          </button>
        </div>
      )}

      {view === "tree" && (
        <div className="mpat-workspace">
          <aside className="mpat-control-panel">
            <div className="mpat-panel-heading">
              <div>
                <span>GÖRÜNÜM</span>
                <h2>Ağaç Kontrolü</h2>
              </div>
              <Icon name="tree" />
            </div>

            <label className="mpat-label">Başlangıç hesapları</label>
            <div className="mpat-segmented">
              <button
                type="button"
                className={rootFilter === "all" ? "is-active" : ""}
                onClick={() => setRootFilter("all")}
              >
                Tümü
              </button>
              <button
                type="button"
                className={rootFilter === "active" ? "is-active" : ""}
                onClick={() => setRootFilter("active")}
              >
                Aktif
              </button>
              <button
                type="button"
                className={rootFilter === "issue" ? "is-active" : ""}
                onClick={() => setRootFilter("issue")}
              >
                Sorunlu
              </button>
            </div>

            <label className="mpat-check">
              <input
                type="checkbox"
                checked={compactTree}
                onChange={(event) => setCompactTree(event.target.checked)}
              />
              <span>
                <b>Kompakt görünüm</b>
                <small>Kalabalık ekiplerde daha fazla satır gösterir.</small>
              </span>
            </label>

            <div className="mpat-guide">
              <strong>Güvenli kullanım</strong>
              <ol>
                <li>
                  Kullanıcıya basıp mevcut sponsorunu ve ekibini kontrol edin.
                </li>
                <li>
                  Tutma simgesinden taşıma başlatın veya kartı sürükleyin.
                </li>
                <li>Yeni sponsor üzerine bırakıp ön izlemeyi inceleyin.</li>
                <li>Gerekçeyi yazıp işlemi açıkça onaylayın.</li>
              </ol>
            </div>

            <div className="mpat-safety-note">
              <Icon name="wallet" />
              <div>
                <strong>Cüzdan güvenliği</strong>
                <span>
                  Taşıma; bakiyeyi, kazanılmış puanı, harcama geçmişini ve görev
                  kayıtlarını değiştirmez.
                </span>
              </div>
            </div>
          </aside>

          <main className="mpat-tree-panel">
            <div className="mpat-section-heading">
              <div>
                <span>REFERANS HİYERARŞİSİ</span>
                <h2>Tüm Ekipler</h2>
                <p>
                  Kişiye basarak ayrıntısını; oka basarak doğrudan ve alt
                  ekibini açın.
                </p>
              </div>
              {summary.treeVersion && (
                <small>Ağaç sürümü: {summary.treeVersion}</small>
              )}
            </div>

            <div
              className="mpat-tree"
              role="tree"
              aria-label="MesPuan referans ağacı"
            >
              {treeLoading && rootIds.length === 0 ? (
                <LoadingBlock text="Referans ağacı yükleniyor..." />
              ) : rootIds.length ? (
                rootIds.map((id) => <TreeNode key={id} nodeId={id} />)
              ) : (
                <EmptyState
                  icon="tree"
                  title="Ağaç kaydı bulunamadı"
                  description="Seçilen filtrede gösterilecek kök hesap bulunmuyor."
                />
              )}
              {branchMeta.root?.hasMore && (
                <button
                  type="button"
                  className="mpat-load-more"
                  onClick={() => loadRoots({ append: true })}
                  disabled={treeLoading}
                >
                  {treeLoading ? "Yükleniyor..." : "Daha fazla kök hesap yükle"}
                </button>
              )}
            </div>
          </main>

          <aside className="mpat-detail-panel">
            {!selectedNode ? (
              <EmptyState
                icon="user"
                title="Bir kullanıcı seçin"
                description="Sponsor, ekip, davet kodu ve salt okunur MesPuan bilgileri burada görünecek."
              />
            ) : selectedLoading ? (
              <LoadingBlock text="Kullanıcı ayrıntıları yükleniyor..." />
            ) : (
              <>
                <div className="mpat-detail-header">
                  <UserSummary node={selectedNode} />
                  <StatusPill
                    status={selectedNode.active ? "active" : "inactive"}
                  >
                    {selectedNode.active ? "Aktif hesap" : "Pasif hesap"}
                  </StatusPill>
                </div>



<div className="mpat-detail-actions">

  <button
    type="button"
    className="mpat-btn is-primary"
    onClick={() => beginMoveWithSearch(selectedNode.id)}
  >
    <Icon name="search" /> Sponsor Ara ve Taşı
  </button>

  <button
    type="button"
    className="mpat-btn is-soft"
    onClick={() => openTeam(selectedNode.id)}
  >
    <Icon name="users" /> Ekibi Gör
  </button>

  <button
    type="button"
    className="mpat-btn is-danger-soft"
    onClick={() => openDeleteUser(selectedNode.id)}
  >
    <Icon name="trash" /> Kullanıcıyı Sil
  </button>

</div>



                <section className="mpat-detail-section">
                  <h3>Bağlantı Bilgileri</h3>
                  <dl>
                    <div>
                      <dt>Mevcut sponsor</dt>
                      <dd>
                        {selectedNode.sponsorName ||
                          (selectedNode.sponsorId
                            ? `Kullanıcı #${selectedNode.sponsorId}`
                            : "Kök hesap")}
                      </dd>
                    </div>
                    <div>
                      <dt>Sponsor ID</dt>
                      <dd>{selectedNode.sponsorId || "—"}</dd>
                    </div>
                    <div>
                      <dt>Doğrudan ekip</dt>
                      <dd>{formatNumber(selectedNode.directCount)} kişi</dd>
                    </div>
                    <div>
                      <dt>Toplam alt ekip</dt>
                      <dd>{formatNumber(selectedNode.teamCount)} kişi</dd>
                    </div>
                    <div>
                      <dt>Ağaç seviyesi</dt>
                      <dd>{selectedNode.depth || "—"}</dd>
                    </div>
                    <div>
                      <dt>Bağlantı durumu</dt>
                      <dd>{selectedNode.referralStatus || "—"}</dd>
                    </div>
                  </dl>
                </section>

                <section className="mpat-detail-section">
                  <div className="mpat-title-row">
                    <h3>Aktif Davet Kodu</h3>
                    <StatusPill
                      status={selectedNode.referralCode ? "active" : "warning"}
                    >
                      {selectedNode.referralCode ? "Mevcut" : "Bulunamadı"}
                    </StatusPill>
                  </div>
                  {selectedNode.referralCode ? (
                    <button
                      type="button"
                      className="mpat-copy-value"
                      onClick={() =>
                        copyText(selectedNode.referralCode, "Davet kodu")
                      }
                    >
                      <span>
                        <b>{selectedNode.referralCode}</b>
                        <small>
                          Kod ID: {selectedNode.referralCodeId || "—"}
                        </small>
                      </span>
                      <Icon name="copy" />
                    </button>
                  ) : (
                    <p className="mpat-inline-warning">
                      <Icon name="alert" /> Bu kullanıcının aktif davet kodu
                      görünmüyor.
                    </p>
                  )}
                </section>

                <section className="mpat-detail-section is-wallet">
                  <div className="mpat-title-row">
                    <h3>MesPuan Özeti</h3>
                    <span className="mpat-readonly-mini">
                      <Icon name="eye" size={14} /> Salt okunur
                    </span>
                  </div>
                  {selectedNode.wallet.exists ? (
                    <>
                      <div className="mpat-balance">
                        <span>Güncel bakiye</span>
                        <strong>
                          {formatNumber(selectedNode.wallet.balance)}{" "}
                          <small>MP</small>
                        </strong>
                      </div>
                      <div className="mpat-wallet-grid">
                        <div>
                          <span>Toplam kazanılan</span>
                          <b>
                            {formatNumber(selectedNode.wallet.lifetimeEarned)}{" "}
                            MP
                          </b>
                        </div>
                        <div>
                          <span>Toplam kullanılan</span>
                          <b>
                            {formatNumber(selectedNode.wallet.lifetimeSpent)} MP
                          </b>
                        </div>
                      </div>
                      <StatusPill status={selectedNode.wallet.status}>
                        {selectedNode.wallet.status === "active"
                          ? "Aktif cüzdan"
                          : selectedNode.wallet.status}
                      </StatusPill>
                    </>
                  ) : (
                    <p className="mpat-muted-copy">
                      Bu kullanıcı için MesPuan cüzdanı bulunamadı.
                    </p>
                  )}
                  <p className="mpat-wallet-notice">
                    <Icon name="lock" size={14} /> Bu ekrandan bakiye veya
                    geçmiş puan değiştirilemez.
                  </p>
                </section>

                <section className="mpat-detail-section">
                  <h3>Hesap Bilgileri</h3>
                  <dl>
                    <div>
                      <dt>Telefon</dt>
                      <dd>{selectedNode.phone || "—"}</dd>
                    </div>
                    <div>
                      <dt>Kayıt tarihi</dt>
                      <dd>{formatDate(selectedNode.joinedAt)}</dd>
                    </div>
                    <div>
                      <dt>Son güncelleme</dt>
                      <dd>{formatDate(selectedNode.updatedAt)}</dd>
                    </div>
                    <div>
                      <dt>Ağaç sağlığı</dt>
                      <dd>
                        <StatusPill status={selectedNode.healthStatus}>
                          {selectedNode.healthStatus === "healthy"
                            ? "Sağlıklı"
                            : selectedNode.healthStatus}
                        </StatusPill>
                      </dd>
                    </div>
                  </dl>
                </section>
              </>
            )}
          </aside>
        </div>



      )}

      {view === "deviceAccess" && (
        <section className="mpat-view-card">

          <div className="mpat-section-heading">
            <span>
              MESPUAN CİHAZ YÖNETİMİ
            </span>

            <h2>
              Cihaz Erişimini Sıfırla
            </h2>

            <p>
              Başka bir MesYat hesabına bağlı olduğu için
              MesPuan kazanamayan kullanıcının e-posta
              adresini yazın.
            </p>
          </div>

          <form
            onSubmit={submitDeviceReset}
            style={{
              maxWidth: 720,
              padding: 20,
            }}
          >

            {deviceResetError && (
              <div className="mpat-inline-error">
                <Icon name="alert" />
                <span>
                  {deviceResetError}
                </span>
              </div>
            )}

            <label className="mpat-field">
              <span>
                Kullanıcı e-posta adresi <b>*</b>
              </span>

              <input
                type="email"
                value={deviceResetEmail}
                onChange={(event) => {
                  setDeviceResetEmail(
                    event.target.value,
                  );

                  setDeviceResetError("");
                  setDeviceResetResult(null);
                }}
                placeholder="Örnek: fuat3247@gmail.com"
                autoComplete="off"
                disabled={deviceResetBusy}
              />

              <small>
                Kullanıcının MesYat'a kayıtlı e-posta
                adresini yazın.
              </small>
            </label>

            <div
              className="mpat-unchanged"
              style={{
                marginTop: 16,
              }}
            >
              <Icon name="lock" />

              <div>
                <strong>
                  Güvenli tek kullanımlık işlem
                </strong>

                <p>
                  Bu işlem kullanıcının MesPuan
                  bakiyesini, kazanılmış puanlarını,
                  sponsorunu, ekibini, davet kodunu
                  veya görev geçmişini değiştirmez.
                  Yalnızca cihaz erişimine bir defalık
                  izin verir.
                </p>
              </div>
            </div>

            {deviceResetResult && (
              <div
                className="mpat-unchanged"
                style={{
                  marginTop: 12,
                }}
              >
                <Icon name="check" />

                <div>
                  <strong>
                    Cihaz erişimi hazır
                  </strong>

                  <p>
                    {deviceResetResult.user_name ||
                      "Kullanıcı"}{" "}
                    ·{" "}
                    {deviceResetResult.email}
                    {" · "}
                    ID:{" "}
                    {deviceResetResult.user_id}
                  </p>

                  <p>
                    Kullanıcı şimdi problem yaşadığı
                    telefondan MesPuan işlemini tekrar
                    denemelidir. İzin kullanıldığında
                    otomatik kapanır.
                  </p>

                  {deviceResetResult.expires_at && (
                    <p>
                      Son kullanım:{" "}
                      {formatDate(
                        deviceResetResult.expires_at,
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="mpat-modal-actions">

              <button
                type="submit"
                className="mpat-btn is-primary"
                disabled={
                  deviceResetBusy ||
                  !deviceResetEmail.trim()
                }
              >

                {deviceResetBusy ? (
                  <>
                    <span className="mpat-spinner is-small" />
                    Hazırlanıyor...
                  </>
                ) : (
                  <>
                    <Icon name="lock" />
                    Cihaz Erişimini Sıfırla
                  </>
                )}

              </button>

            </div>



          </form>

          <div
            style={{
              borderTop: "1px solid var(--mpat-line)",
              marginTop: 6,
              padding: 20,
              maxWidth: 760,
            }}
          >
            <div className="mpat-section-heading">

              <span>
                TAM HESAP TEMİZLİĞİ
              </span>

              <h2>
                E-posta ile Her Şeyi Temizle
              </h2>

              <p>
                Ana kullanıcı kaydı daha önce silinmiş olsa bile;
                aynı e-postaya bağlı eski kullanıcı ID'lerini,
                cüzdanları, cihaz kayıtlarını, cihaz sıfırlama
                geçmişini, görev/video oturumlarını ve diğer
                MesPuan kalıntılarını tek işlemde temizler.
              </p>

            </div>

            <form
              onSubmit={submitEmailPurge}
              style={{
                marginTop: 16,
              }}
            >

              <div
                className="mpat-inline-error"
                style={{
                  marginBottom: 16,
                }}
              >
                <Icon name="trash" />

                <span>
                  Bu işlem geri alınamaz. Başka aktif kullanıcılar
                  bu hesabın altında bulunuyorsa backend işlemi
                  güvenlik için otomatik durdurur. Sponsorların
                  geçmişte kazandığı MesPuanlar değiştirilmez.
                </span>
              </div>

              {purgeError && (
                <div className="mpat-inline-error">
                  <Icon name="alert" />
                  <span>
                    {purgeError}
                  </span>
                </div>
              )}

              <label className="mpat-field">

                <span>
                  Temizlenecek e-posta adresi <b>*</b>
                </span>

                <input
                  type="email"
                  value={purgeEmail}
                  onChange={(event) => {
                    setPurgeEmail(
                      event.target.value,
                    );

                    setPurgeError("");
                    setPurgeResult(null);
                  }}
                  placeholder="Örnek: kullanici@gmail.com"
                  autoComplete="off"
                  disabled={purgeBusy}
                />

              </label>

              <label
                className="mpat-field"
                style={{
                  marginTop: 14,
                }}
              >

                <span>
                  Onay <b>*</b>
                </span>

                <input
                  type="text"
                  value={purgeConfirmation}
                  onChange={(event) => {
                    setPurgeConfirmation(
                      event.target.value,
                    );

                    setPurgeError("");
                  }}
                  placeholder="TAM SİL"
                  autoComplete="off"
                  disabled={purgeBusy}
                />

                <small>
                  Devam etmek için TAM SİL yazın.
                </small>

              </label>

              {purgeResult && (
                <div
                  className="mpat-unchanged"
                  style={{
                    marginTop: 14,
                  }}
                >
                  <Icon name="check" />

                  <div>

                    <strong>
                      Tam temizlik tamamlandı
                    </strong>

                    <p>
                      {purgeResult.email ||
                        "E-posta"}
                      {" · "}
                      Kullanıcı ID sayısı:{" "}
                      {formatNumber(
                        purgeResult
                          .purged_user_count ||
                          0,
                      )}
                      {" · "}
                      Cüzdan sayısı:{" "}
                      {formatNumber(
                        purgeResult
                          .purged_wallet_count ||
                          0,
                      )}
                    </p>

                  </div>
                </div>
              )}

              <div className="mpat-modal-actions">

                <button
                  type="submit"
                  className="mpat-btn is-danger"
                  disabled={
                    purgeBusy ||
                    !purgeEmail.trim() ||
                    purgeConfirmation
                      .trim()
                      .toLocaleUpperCase(
                        "tr-TR",
                      ) !== "TAM SİL"
                  }
                >

                  {purgeBusy ? (
                    <>
                      <span className="mpat-spinner is-small" />
                      {" "}
                      Tam temizleniyor...
                    </>
                  ) : (
                    <>
                      <Icon name="trash" />
                      {" "}
                      E-posta ile Tam Temizle
                    </>
                  )}

                </button>

              </div>

            </form>

          </div>

        </section>
      )}

      {view === "health" && (


        <section className="mpat-view-card">

          <div className="mpat-section-heading is-row">
            <div>
              <span>AĞAÇ SAĞLIK MERKEZİ</span>
              <h2>Bağlantı Kontrolleri</h2>
              <p>
                Bu alan yalnızca sorunları gösterir; otomatik değişiklik yapmaz.
              </p>
            </div>
            <select
              value={healthFilter}
              onChange={(event) => setHealthFilter(event.target.value)}
            >
              <option value="all">Tüm sorun türleri</option>
              {Object.entries(HEALTH_TYPES)
                .filter(
                  ([key]) =>
                    !["missing_sponsor", "duplicate_sponsor"].includes(key),
                )
                .map(([key, item]) => (
                  <option key={key} value={key}>
                    {item.label}
                  </option>
                ))}
            </select>
          </div>

          {healthLoading ? (
            <LoadingBlock text="Ağaç sağlık kontrolü çalıştırılıyor..." />
          ) : healthIssues.length ? (
            <div className="mpat-health-list">
              {healthIssues.map((issue, index) => {
                const type = String(
                  firstDefined(issue.type, issue.issue_type, "broken_link"),
                ).toLowerCase();
                const config = HEALTH_TYPES[type] || HEALTH_TYPES.broken_link;
                const userId = asPositiveInt(
                  firstDefined(issue.user_id, issue.id),
                );
                return (
                  <article
                    className={cx("mpat-health-item", `is-${config.tone}`)}
                    key={`${type}-${userId || index}`}
                  >
                    <span className="mpat-health-icon">
                      <Icon name={config.icon} />
                    </span>
                    <div className="mpat-health-copy">
                      <span>{config.label}</span>
                      <strong>
                        {firstDefined(
                          issue.user_name,
                          issue.name,
                          issue.ad,
                          userId ? `Kullanıcı #${userId}` : "Sistem kaydı",
                        )}
                      </strong>
                      <p>
                        {firstDefined(
                          issue.message,
                          issue.description,
                          issue.detail,
                          "Bu bağlantı yönetici tarafından incelenmelidir.",
                        )}
                      </p>
                    </div>
                    <div className="mpat-health-meta">
                      <small>
                        {firstDefined(issue.email, issue.user_email, "")}
                      </small>
                      {userId && <b>ID: {userId}</b>}
                    </div>
                    {userId && (
                      <button
                        type="button"
                        className="mpat-btn is-soft"
                        onClick={() => selectUser(userId)}
                      >
                        <Icon name="eye" /> İncele
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon="health"
              title="Ağaçta sorun görünmüyor"
              description="Seçilen kapsamda sponsorluk bağlantıları sağlıklı görünüyor."
            />
          )}
          {healthMeta.total > 0 && (
            <p className="mpat-result-count">
              Toplam {formatNumber(healthMeta.total)} sağlık kaydı
            </p>
          )}
        </section>
      )}

      {view === "history" && (
        <section className="mpat-view-card">
          <div className="mpat-section-heading">
            <span>DENETİM KAYITLARI</span>
            <h2>Taşıma İşlem Geçmişi</h2>
            <p>
              Kim, kimi, ne zaman ve hangi gerekçeyle taşıdı ayrıntılı olarak
              izlenir.
            </p>
          </div>
          {historyLoading ? (
            <LoadingBlock text="İşlem geçmişi yükleniyor..." />
          ) : historyItems.length ? (
            <div className="mpat-history-list">
              {historyItems.map((item, index) => {
                const moveId = asPositiveInt(
                  firstDefined(item.id, item.move_id, item.audit_id),
                );
                const canUndo = asBoolean(
                  firstDefined(item.can_undo, item.canUndo),
                  false,
                );
                return (
                  <article className="mpat-history-item" key={moveId || index}>
                    <span className="mpat-history-mark">
                      <Icon name={item.status === "undone" ? "undo" : "move"} />
                    </span>
                    <div className="mpat-history-main">
                      <div className="mpat-history-title">
                        <strong>
                          {firstDefined(
                            item.source_user_name,
                            item.user_name,
                            item.source_name,
                            "Kullanıcı",
                          )}
                        </strong>
                        <span>
                          <Icon name="arrow" size={15} />{" "}
                          {firstDefined(
                            item.new_sponsor_name,
                            item.target_name,
                            "Yeni sponsor",
                          )}
                        </span>
                        <StatusPill status={item.status || "completed"}>
                          {item.status === "undone"
                            ? "Geri alındı"
                            : "Tamamlandı"}
                        </StatusPill>
                      </div>
                      <p>
                        {firstDefined(
                          item.reason,
                          item.description,
                          "Gerekçe bilgisi bulunmuyor.",
                        )}
                      </p>
                      <div className="mpat-history-details">
                        <span>
                          Eski sponsor:{" "}
                          <b>{firstDefined(item.old_sponsor_name, "—")}</b>
                        </span>
                        <span>
                          Birlikte taşınan ekip:{" "}
                          <b>
                            {formatNumber(
                              firstDefined(
                                item.team_count,
                                item.moved_descendant_count,
                              ),
                            )}{" "}
                            kişi
                          </b>
                        </span>
                        <span>
                          İşlemi yapan:{" "}
                          <b>
                            {firstDefined(
                              item.admin_name,
                              item.admin_email,
                              "MesYat Admin",
                            )}
                          </b>
                        </span>
                        <span>
                          Tarih:{" "}
                          <b>
                            {formatDate(
                              firstDefined(item.created_at, item.moved_at),
                            )}
                          </b>
                        </span>
                      </div>
                    </div>
                    {canUndo && item.status !== "undone" && (
                      <button
                        type="button"
                        className="mpat-btn is-danger-soft"
                        onClick={() => openUndo(item)}
                      >
                        <Icon name="undo" /> Güvenli geri al
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          ) : (
            <EmptyState
              icon="history"
              title="Taşıma kaydı bulunamadı"
              description="Gerçekleştirilen güvenli taşıma işlemleri burada denetim kaydı olarak görünecek."
            />
          )}
          {historyMeta.total > 0 && (
            <p className="mpat-result-count">
              Toplam {formatNumber(historyMeta.total)} işlem kaydı
            </p>
          )}
        </section>
      )}



      {teamDialog && (
        <Modal
          title={`${teamDialog.owner.name} · Tüm Ekip`}
          description="Doğrudan ve tüm alt ekip tek ekranda gösterilir."
          onClose={() => setTeamDialog(null)}
          size="large"
        >
          {teamDialog.loading ? (
            <LoadingBlock text="Tüm ekip hazırlanıyor..." />
          ) : teamDialog.error ? (
            <div className="mpat-inline-error">
              <Icon name="alert" />
              <span>{teamDialog.error}</span>
            </div>
          ) : (
            <>
              <div className="mpat-team-summary">

                <div>
                  <span>DOĞRUDAN EKİP</span>
                  <strong>
                    {formatNumber(teamDialog.owner.directCount)}
                  </strong>
                </div>

                <div>
                  <span>TOPLAM ALT EKİP</span>
                  <strong>
                    {formatNumber(teamDialog.members.length)}
                  </strong>
                </div>

                <div>
                  <span>AĞAÇ SEVİYESİ</span>
                  <strong>
                    {teamDialog.owner.depth || "—"}
                  </strong>
                </div>

              </div>

              {teamDialog.members.length ? (
                <div className="mpat-team-list">

                  {teamDialog.members.map((member) => (
                    <button
                      type="button"
                      className="mpat-team-row"
                      key={member.id}
                      onClick={() => {
                        setTeamDialog(null);
                        selectUser(member.id);
                      }}
                    >

                      <span className="mpat-team-level">
                        {member.teamLevel}. seviye
                      </span>

                      <Avatar node={member} />

                      <span className="mpat-team-person">
                        <strong>{member.name}</strong>
                        <small>
                          {member.email ||
                            `Kullanıcı #${member.id}`}
                        </small>
                      </span>

                      <span className="mpat-team-sponsor">
                        <small>Sponsor</small>
                        <b>
                          {member.sponsorName ||
                            `#${member.sponsorId || "—"}`}
                        </b>
                      </span>

                      <span className="mpat-team-count">
                        <b>
                          {formatNumber(member.directCount)}
                        </b>
                        direkt
                      </span>

                    </button>
                  ))}

                </div>
              ) : (
                <EmptyState
                  icon="users"
                  title="Bu kullanıcının ekibi yok"
                  description="Doğrudan veya alt ekip kaydı bulunmuyor."
                />
              )}
            </>
          )}
        </Modal>
      )}


      {deleteDialog && (


<Modal
  title="Kullanıcıyı Kalıcı Sil"
  description="Bu işlem seçili e-postaya bağlı mevcut ve geçmiş hesap/MesPuan kalıntılarını tek işlemde temizler."

          onClose={() =>
            !deleteDialog.submitting &&
            setDeleteDialog(null)
          }
        >

          <UserSummary
            node={deleteDialog.node}
            compact
          />

          {deleteDialog.error && (
            <div
              className="mpat-inline-error"
              style={{ marginTop: 14 }}
            >
              <Icon name="alert" />
              <span>{deleteDialog.error}</span>
            </div>
          )}

          {deleteDialog.node.teamCount > 0 ? (

            <div className="mpat-delete-warning is-blocked">
              <Icon name="users" />

              <div>
                <strong>
                  Bu kullanıcı şu anda silinemez
                </strong>

                <p>
                  Altında{" "}
                  {formatNumber(
                    deleteDialog.node.teamCount,
                  )}{" "}
                  kişi var. Ağacı bozmamak için önce
                  ekibi başka sponsora taşıyın.
                </p>
              </div>
            </div>

          ) : (

            <div className="mpat-delete-warning">
              <Icon name="trash" />

              <div>
                <strong>
                  Kalıcı veritabanı silme
                </strong>

                <p>
                  Yalnızca bu kullanıcıya ait hesap,
                  cihaz, davet kodu, bağlantı,
                  cüzdan ve kendi MesPuan kayıtları
                  temizlenecektir.
                </p>
              </div>
            </div>

          )}

          <div className="mpat-modal-actions">

            <button
              type="button"
              className="mpat-btn is-ghost"
              onClick={() =>
                setDeleteDialog(null)
              }
              disabled={deleteDialog.submitting}
            >
              Vazgeç
            </button>

            <button
              type="button"
              className="mpat-btn is-danger"
              onClick={commitDeleteUser}
              disabled={
                deleteDialog.submitting ||
                deleteDialog.node.teamCount > 0
              }
            >

              {deleteDialog.submitting ? (
                <>
                  <span className="mpat-spinner is-small" />
                  {" "}Siliniyor...
                </>
              ) : (
                <>
                  <Icon name="trash" />
                  {" "}Evet, Kalıcı Sil
                </>
              )}

            </button>

          </div>

        </Modal>
      )}


      {moveDialog && (
        <Modal
          title="Güvenli Taşıma Ön İzlemesi"


description={
  moveDialog?.source && moveDialog?.target
    ? `${moveDialog.source.name} ve altındaki ${formatNumber(
        moveDialog.source.teamCount,
      )} kişiyi ${moveDialog.target.name} altına taşımak istediğinize emin misiniz?`
    : "Aşağıdaki bilgiler doğrulanmadan referans ağacında hiçbir değişiklik yapılmaz."
}

          onClose={() => !moveDialog.submitting && setMoveDialog(null)}
          size="large"
        >
          {moveDialog.loading ? (
            <LoadingBlock text="Döngü, sponsor, davet kodu ve ekip kontrolleri yapılıyor..." />
          ) : (
            <>
              {moveDialog.error && (
                <div className="mpat-inline-error">
                  <Icon name="alert" />
                  <span>{moveDialog.error}</span>
                </div>
              )}
              {moveDialog.preview && (
                <>
                  <div className="mpat-move-people">
                    <div>
                      <span>TAŞINACAK KİŞİ VE EKİBİ</span>
                      <UserSummary node={moveDialog.source} compact />
                    </div>
                    <span className="mpat-move-arrow">
                      <Icon name="arrow" size={24} />
                    </span>
                    <div>
                      <span>YENİ SPONSOR</span>
                      <UserSummary node={moveDialog.target} compact />
                    </div>
                  </div>

                  <div className="mpat-preview-grid">
                    <div>
                      <span>Eski sponsor</span>
                      <strong>
                        {firstDefined(
                          moveDialog.preview.old_sponsor_name,
                          moveDialog.source.sponsorName,
                          "Kök hesap",
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Yeni sponsor</span>
                      <strong>
                        {firstDefined(
                          moveDialog.preview.new_sponsor_name,
                          moveDialog.target.name,
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Birlikte taşınacak ekip</span>
                      <strong>
                        {formatNumber(
                          firstDefined(
                            moveDialog.preview.team_count,
                            moveDialog.preview.descendant_count,
                            moveDialog.source.teamCount,
                          ),
                        )}{" "}
                        kişi
                      </strong>
                    </div>
                    <div>
                      <span>Doğrudan üst bağlantı</span>
                      <strong>Yalnızca 1 kayıt değişir</strong>
                    </div>
                    <div>
                      <span>Yeni sponsor davet kodu</span>
                      <strong>
                        {firstDefined(
                          moveDialog.preview.target_referral_code,
                          moveDialog.target.referralCode,
                          "Kontrol edildi",
                        )}
                      </strong>
                    </div>
                    <div>
                      <span>Döngü kontrolü</span>
                      <strong className="is-success">
                        <Icon name="check" size={15} /> Uygun
                      </strong>
                    </div>
                  </div>

                  <div className="mpat-unchanged">
                    <Icon name="lock" />
                    <div>
                      <strong>Değişmeyecek veriler</strong>
                      <p>
                        Alt ekip bağlantıları, mevcut MesPuan bakiyeleri,
                        kazanılmış ve harcanmış puanlar, görev kayıtları,
                        hareket geçmişi ve cüzdan durumu korunacaktır.
                      </p>
                    </div>
                  </div>

                  {Array.isArray(moveDialog.preview.warnings) &&
                    moveDialog.preview.warnings.length > 0 && (
                      <div className="mpat-warnings">
                        <strong>Sunucu uyarıları</strong>
                        {moveDialog.preview.warnings.map((warning, index) => (
                          <p key={index}>
                            <Icon name="alert" size={15} /> {String(warning)}
                          </p>
                        ))}
                      </div>
                    )}

                  <label className="mpat-field">
                    <span>
                      İşlem gerekçesi <b>*</b>
                    </span>
                    <textarea
                      value={moveDialog.reason}
                      onChange={(event) =>
                        setMoveDialog((current) => ({
                          ...current,
                          reason: event.target.value,
                          error: "",
                        }))
                      }
                      placeholder="Örnek: Kullanıcı kayıt sırasında yanlış sponsor koduyla bağlandığı için doğru ekibe taşınıyor."
                      rows={3}
                    />
                    <small>
                      En az 15 karakter. Bu metin değiştirilemez denetim kaydına
                      yazılır.
                    </small>
                  </label>
                  <label className="mpat-field">
                    <span>
                      İşlemi onaylamak için <b>TAŞI</b> yazın
                    </span>
                    <input
                      value={moveDialog.confirmation}
                      onChange={(event) =>
                        setMoveDialog((current) => ({
                          ...current,
                          confirmation: event.target.value,
                          error: "",
                        }))
                      }
                      autoComplete="off"
                      placeholder="TAŞI"
                    />
                  </label>

                  <div className="mpat-modal-actions">
                    <button
                      type="button"
                      className="mpat-btn is-ghost"
                      onClick={() => setMoveDialog(null)}
                      disabled={moveDialog.submitting}
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      className="mpat-btn is-primary"
                      onClick={commitMove}
                      disabled={
                        moveDialog.submitting ||
                        moveDialog.confirmation
                          .trim()
                          .toLocaleUpperCase("tr-TR") !== "TAŞI" ||
                        moveDialog.reason.trim().length < 15
                      }
                    >
                      {moveDialog.submitting ? (
                        <>
                          <span className="mpat-spinner is-small" />{" "}
                          Taşınıyor...
                        </>
                      ) : (
                        <>
                          <Icon name="move" /> Ekibiyle Birlikte Taşı
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </Modal>
      )}

      {undoDialog && (
        <Modal
          title="Taşımayı Güvenli Geri Al"
          description="Geri alma yalnızca ağaç sürümü ve mevcut bağlantılar ilk işlemle uyumluysa uygulanır."
          onClose={() => !undoDialog.submitting && setUndoDialog(null)}
        >
          {undoDialog.loading ? (
            <LoadingBlock text="Geri alma güvenlik kontrolleri yapılıyor..." />
          ) : (
            <>
              {undoDialog.error && (
                <div className="mpat-inline-error">
                  <Icon name="alert" />
                  <span>{undoDialog.error}</span>
                </div>
              )}
              {undoDialog.preview && (
                <>
                  <div className="mpat-unchanged is-warning">
                    <Icon name="undo" />
                    <div>
                      <strong>Yalnızca sponsor bağlantısı geri alınır</strong>
                      <p>
                        MesPuan bakiyeleri ve hareket geçmişi kesinlikle
                        değiştirilmez. Sonraki ağ değişiklikleriyle çakışma
                        varsa sunucu işlemi durdurur.
                      </p>
                    </div>
                  </div>
                  <label className="mpat-field">
                    <span>
                      Geri alma gerekçesi <b>*</b>
                    </span>
                    <textarea
                      rows={3}
                      value={undoDialog.reason}
                      onChange={(event) =>
                        setUndoDialog((current) => ({
                          ...current,
                          reason: event.target.value,
                          error: "",
                        }))
                      }
                      placeholder="Bu işlemin neden geri alındığını ayrıntılı yazın."
                    />
                  </label>
                  <label className="mpat-field">
                    <span>
                      Onaylamak için <b>GERİ AL</b> yazın
                    </span>
                    <input
                      value={undoDialog.confirmation}
                      onChange={(event) =>
                        setUndoDialog((current) => ({
                          ...current,
                          confirmation: event.target.value,
                          error: "",
                        }))
                      }
                      placeholder="GERİ AL"
                    />
                  </label>
                  <div className="mpat-modal-actions">
                    <button
                      type="button"
                      className="mpat-btn is-ghost"
                      onClick={() => setUndoDialog(null)}
                      disabled={undoDialog.submitting}
                    >
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      className="mpat-btn is-danger"
                      onClick={commitUndo}
                      disabled={
                        undoDialog.submitting ||
                        undoDialog.confirmation
                          .trim()
                          .toLocaleUpperCase("tr-TR") !== "GERİ AL" ||
                        undoDialog.reason.trim().length < 15
                      }
                    >
                      {undoDialog.submitting
                        ? "Geri alınıyor..."
                        : "İşlemi Güvenli Geri Al"}
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </Modal>
      )}

      {toast && (
        <div className={cx("mpat-toast", `is-${toast.type}`)} role="status">
          <span>
            <Icon
              name={
                toast.type === "error"
                  ? "alert"
                  : toast.type === "info"
                    ? "eye"
                    : "check"
              }
              size={17}
            />
          </span>
          <p>{toast.message}</p>
          <button
            type="button"
            onClick={() => setToast(null)}
            aria-label="Bildirimi kapat"
          >
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

function Style() {
  return (
    <style>{`
      .mpat-shell {
        --mpat-blue: #0b4d8b;
        --mpat-blue-dark: #07345f;
        --mpat-blue-soft: #eaf4fd;
        --mpat-cyan: #0e8dac;
        --mpat-orange: #f28a1a;
        --mpat-orange-soft: #fff3e4;
        --mpat-green: #17845b;
        --mpat-green-soft: #eaf8f1;
        --mpat-red: #c63e32;
        --mpat-red-soft: #fff0ee;
        --mpat-purple: #7254a8;
        --mpat-ink: #122238;
        --mpat-muted: #6d7d91;
        --mpat-line: #dce5ee;
        --mpat-surface: #f4f7fa;
        --mpat-shadow: 0 12px 35px rgba(8, 38, 67, .08);
        min-height: 100%;
        color: var(--mpat-ink);
        background: var(--mpat-surface);
        font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
        font-size: 14px;
        line-height: 1.45;
      }
      .mpat-shell *, .mpat-shell *::before, .mpat-shell *::after { box-sizing: border-box; }
      .mpat-shell button, .mpat-shell input, .mpat-shell textarea, .mpat-shell select { font: inherit; }
      .mpat-shell button { cursor: pointer; }
      .mpat-shell button:disabled { cursor: not-allowed; opacity: .55; }
      .mpat-hero { display: flex; align-items: center; justify-content: space-between; gap: 24px; min-height: 180px; padding: 32px clamp(20px, 4vw, 52px); color: #fff; background: radial-gradient(circle at 85% 18%, rgba(59, 164, 213, .38), transparent 26%), linear-gradient(118deg, #062e56, #0b4d8b 58%, #0d6d9f); }
      .mpat-eyebrow { display: block; margin-bottom: 8px; color: #ffbf73; font-size: 11px; font-weight: 850; letter-spacing: 1.2px; }
      .mpat-hero h1 { margin: 0; font-size: clamp(25px, 3vw, 38px); letter-spacing: -.6px; }
      .mpat-hero p { max-width: 720px; margin: 9px 0 0; color: #d7e8f5; font-size: 14px; }
      .mpat-hero-actions { display: flex; flex-wrap: wrap; align-items: center; justify-content: flex-end; gap: 10px; }
      .mpat-readonly-badge { display: inline-flex; align-items: center; gap: 7px; min-height: 42px; padding: 0 13px; border: 1px solid rgba(255,255,255,.22); border-radius: 12px; color: #e6f2fb; background: rgba(255,255,255,.09); font-size: 12px; font-weight: 750; }
      .mpat-btn { display: inline-flex; min-height: 40px; align-items: center; justify-content: center; gap: 7px; border: 1px solid transparent; border-radius: 11px; padding: 9px 14px; font-size: 12px; font-weight: 800; transition: .18s ease; }
      .mpat-btn:hover:not(:disabled) { transform: translateY(-1px); }
      .mpat-btn.is-light { color: var(--mpat-blue); background: #fff; box-shadow: 0 8px 22px rgba(0,0,0,.14); }
      .mpat-btn.is-primary { color: #fff; background: linear-gradient(135deg, var(--mpat-blue-dark), var(--mpat-blue)); box-shadow: 0 8px 18px rgba(11,77,139,.18); }
      .mpat-btn.is-soft { color: var(--mpat-blue); border-color: #cfe0ee; background: #f3f8fc; }
      .mpat-btn.is-ghost { color: #55677d; border-color: var(--mpat-line); background: #fff; }
      .mpat-btn.is-danger { color: #fff; background: var(--mpat-red); }
      .mpat-btn.is-danger-soft { color: var(--mpat-red); border-color: #f0c8c4; background: var(--mpat-red-soft); }
      .mpat-tabs { position: sticky; z-index: 20; top: 0; display: flex; gap: 4px; overflow-x: auto; padding: 9px clamp(14px, 3vw, 36px); border-bottom: 1px solid var(--mpat-line); background: rgba(255,255,255,.96); backdrop-filter: blur(10px); }
      .mpat-tabs button { display: inline-flex; flex: 0 0 auto; align-items: center; gap: 8px; min-height: 42px; border: 0; border-radius: 11px; padding: 0 15px; color: #607186; background: transparent; font-size: 12px; font-weight: 800; }
      .mpat-tabs button.is-active { color: var(--mpat-blue); background: var(--mpat-blue-soft); }
      .mpat-tabs button b { display: grid; min-width: 21px; height: 21px; place-items: center; border-radius: 20px; padding: 0 6px; color: #fff; background: var(--mpat-red); font-size: 10px; }
      .mpat-alert { display: flex; align-items: center; gap: 11px; margin: 16px clamp(14px, 3vw, 36px) 0; padding: 13px 15px; border: 1px solid; border-radius: 13px; }
      .mpat-alert.is-error { color: #8f2b23; border-color: #edc3bf; background: var(--mpat-red-soft); }
      .mpat-alert div { min-width: 0; flex: 1; }
      .mpat-alert strong, .mpat-alert span { display: block; }
      .mpat-alert span { margin-top: 2px; font-size: 12px; }
      .mpat-alert button { border: 0; border-radius: 9px; padding: 8px 11px; color: var(--mpat-red); background: #fff; font-size: 11px; font-weight: 800; }
      .mpat-stats { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; padding: 20px clamp(14px, 3vw, 36px) 0; }
      .mpat-stat { display: flex; min-width: 0; align-items: center; gap: 12px; border: 1px solid var(--mpat-line); border-radius: 16px; padding: 14px; text-align: left; background: #fff; box-shadow: var(--mpat-shadow); }
      button.mpat-stat { width: 100%; }
      .mpat-stat.is-clickable:hover { border-color: #a9c6dd; transform: translateY(-1px); }
      .mpat-stat-icon { display: grid; flex: 0 0 42px; height: 42px; place-items: center; border-radius: 13px; color: var(--mpat-blue); background: var(--mpat-blue-soft); }
      .mpat-stat.is-cyan .mpat-stat-icon { color: var(--mpat-cyan); background: #e8f8fb; }
      .mpat-stat.is-orange .mpat-stat-icon { color: #b8650d; background: var(--mpat-orange-soft); }
      .mpat-stat.is-red .mpat-stat-icon { color: var(--mpat-red); background: var(--mpat-red-soft); }
      .mpat-stat.is-green .mpat-stat-icon { color: var(--mpat-green); background: var(--mpat-green-soft); }
      .mpat-stat.is-purple .mpat-stat-icon { color: var(--mpat-purple); background: #f1edfb; }
      .mpat-stat-copy { min-width: 0; }
      .mpat-stat-copy > span { display: block; overflow: hidden; color: var(--mpat-muted); font-size: 10px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
      .mpat-stat-copy strong { display: inline-block; margin-top: 3px; font-size: 22px; line-height: 1; }
      .mpat-stat-copy small { margin-left: 5px; color: var(--mpat-muted); font-size: 10px; }
      .mpat-search-wrap { position: relative; z-index: 15; padding: 16px clamp(14px, 3vw, 36px) 0; }
      .mpat-search { display: flex; align-items: center; gap: 10px; max-width: 960px; min-height: 48px; margin: 0 auto; border: 1px solid #cbd8e4; border-radius: 14px; padding: 0 14px; background: #fff; box-shadow: 0 9px 25px rgba(8,38,67,.07); }
      .mpat-search > svg { color: var(--mpat-blue); }
      .mpat-search input { min-width: 0; flex: 1; height: 46px; border: 0; outline: 0; color: var(--mpat-ink); font-size: 13px; }
      .mpat-search > button { display: grid; width: 30px; height: 30px; place-items: center; border: 0; border-radius: 8px; color: var(--mpat-muted); background: #f1f5f8; }
      .mpat-search-results { position: absolute; left: 50%; width: min(960px, calc(100% - 28px)); max-height: 430px; overflow-y: auto; transform: translateX(-50%); border: 1px solid var(--mpat-line); border-radius: 0 0 15px 15px; padding: 8px; background: #fff; box-shadow: 0 22px 50px rgba(8,38,67,.18); }
      .mpat-search-title { display: flex; justify-content: space-between; padding: 8px 10px; color: var(--mpat-muted); font-size: 10px; font-weight: 850; text-transform: uppercase; }
      .mpat-search-results > button { display: flex; width: 100%; align-items: center; gap: 10px; border: 0; border-radius: 11px; padding: 9px; text-align: left; background: transparent; }
      .mpat-search-results > button:hover { background: #f1f7fb; }
      .mpat-search-results > button > span:nth-child(2) { min-width: 0; flex: 1; }
      .mpat-search-results strong, .mpat-search-results small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .mpat-search-results strong { font-size: 12px; }
      .mpat-search-results small { margin-top: 2px; color: var(--mpat-muted); font-size: 10px; }
      .mpat-search-results > p { margin: 12px; color: var(--mpat-muted); text-align: center; }
      .mpat-search-meta { display: flex; align-items: center; gap: 6px; color: var(--mpat-muted); font-size: 10px; }
      .mpat-move-banner { display: flex; align-items: center; gap: 12px; margin: 16px clamp(14px, 3vw, 36px) 0; border: 1px solid #f1c58f; border-radius: 14px; padding: 12px 14px; background: #fff8ef; box-shadow: var(--mpat-shadow); }
      .mpat-move-icon { display: grid; flex: 0 0 38px; height: 38px; place-items: center; border-radius: 11px; color: #a85608; background: #ffe6c7; }
      .mpat-move-banner > div { min-width: 0; flex: 1; }
      .mpat-move-banner strong, .mpat-move-banner span { display: block; }
      .mpat-move-banner strong { color: #854406; font-size: 12px; }
      .mpat-move-banner > div span { margin-top: 2px; color: #9b5e24; font-size: 10px; }
      .mpat-move-banner > button { border: 1px solid #e8bb84; border-radius: 9px; padding: 7px 11px; color: #9a4d08; background: #fff; font-size: 11px; font-weight: 800; }
      .mpat-workspace { display: grid; grid-template-columns: minmax(210px, 250px) minmax(480px, 1fr) minmax(280px, 340px); gap: 14px; align-items: start; padding: 16px clamp(14px, 3vw, 36px) 32px; }
      .mpat-control-panel, .mpat-tree-panel, .mpat-detail-panel, .mpat-view-card { border: 1px solid var(--mpat-line); border-radius: 18px; background: #fff; box-shadow: var(--mpat-shadow); }
      .mpat-control-panel, .mpat-detail-panel { position: sticky; top: 72px; }
      .mpat-control-panel { padding: 16px; }
      .mpat-panel-heading { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; }
      .mpat-panel-heading > svg { color: var(--mpat-blue); }
      .mpat-panel-heading span, .mpat-section-heading > div > span, .mpat-section-heading > span { color: var(--mpat-orange); font-size: 9px; font-weight: 900; letter-spacing: .8px; }
      .mpat-panel-heading h2, .mpat-section-heading h2 { margin: 3px 0 0; font-size: 17px; }
      .mpat-label { display: block; margin-bottom: 7px; color: #58697e; font-size: 10px; font-weight: 850; }
      .mpat-segmented { display: grid; grid-template-columns: repeat(3, 1fr); gap: 3px; padding: 3px; border-radius: 10px; background: #edf2f6; }
      .mpat-segmented button { min-height: 33px; border: 0; border-radius: 8px; color: #6b7b8e; background: transparent; font-size: 10px; font-weight: 800; }
      .mpat-segmented button.is-active { color: var(--mpat-blue); background: #fff; box-shadow: 0 3px 10px rgba(8,38,67,.08); }
      .mpat-check { display: flex; gap: 9px; margin-top: 15px; cursor: pointer; }
      .mpat-check input { width: 17px; height: 17px; accent-color: var(--mpat-blue); }
      .mpat-check span { min-width: 0; }
      .mpat-check b, .mpat-check small { display: block; }
      .mpat-check b { font-size: 11px; }
      .mpat-check small { margin-top: 2px; color: var(--mpat-muted); font-size: 9px; }
      .mpat-guide { margin-top: 17px; border-top: 1px solid var(--mpat-line); padding-top: 15px; }
      .mpat-guide strong { font-size: 11px; }
      .mpat-guide ol { margin: 9px 0 0; padding-left: 19px; color: #607186; font-size: 10px; }
      .mpat-guide li + li { margin-top: 6px; }
      .mpat-safety-note { display: flex; gap: 9px; margin-top: 16px; border: 1px solid #cce5da; border-radius: 12px; padding: 11px; color: #246c53; background: var(--mpat-green-soft); }
      .mpat-safety-note > svg { flex: 0 0 auto; }
      .mpat-safety-note strong, .mpat-safety-note span { display: block; }
      .mpat-safety-note strong { font-size: 10px; }
      .mpat-safety-note span { margin-top: 3px; font-size: 9px; }
      .mpat-tree-panel { min-width: 0; overflow: hidden; }
      .mpat-section-heading { padding: 17px 18px; border-bottom: 1px solid var(--mpat-line); }
      .mpat-section-heading.is-row { display: flex; align-items: center; justify-content: space-between; gap: 15px; }
      .mpat-section-heading p { margin: 5px 0 0; color: var(--mpat-muted); font-size: 10px; }
      .mpat-section-heading > small { float: right; margin-top: -18px; color: var(--mpat-muted); font-size: 9px; }
      .mpat-section-heading select { min-width: 210px; height: 40px; border: 1px solid var(--mpat-line); border-radius: 10px; padding: 0 10px; color: var(--mpat-ink); background: #fff; outline: 0; }
      .mpat-tree { min-height: 420px; overflow-x: auto; padding: 10px; }
      .mpat-tree-item { min-width: 620px; }
      .mpat-node { position: relative; display: grid; grid-template-columns: 27px 25px minmax(210px, 1fr) auto auto auto; gap: 7px; align-items: center; min-height: 61px; margin-left: calc(var(--mpat-depth) * 24px); border: 1px solid transparent; border-radius: 12px; padding: 7px 8px; transition: .16s ease; }
      .mpat-node:hover { border-color: #d5e3ee; background: #f7fafc; }
      .mpat-node.is-compact { min-height: 49px; padding-block: 4px; }
      .mpat-node.is-selected { border-color: #8eb7d8; background: #edf6fd; box-shadow: inset 3px 0 var(--mpat-blue); }
      .mpat-node.is-move-source { border-color: #eab36e; background: #fff6ea; box-shadow: inset 3px 0 var(--mpat-orange); }
      .mpat-node.is-drop-target { border-color: var(--mpat-green); background: var(--mpat-green-soft); box-shadow: 0 0 0 3px rgba(23,132,91,.12); }
      .mpat-branch-line { position: absolute; top: -11px; bottom: 50%; left: -13px; width: 13px; border-bottom: 1px solid #ccd8e2; border-left: 1px solid #ccd8e2; border-radius: 0 0 0 7px; pointer-events: none; }
      .mpat-node[style*="--mpat-depth: 0"] .mpat-branch-line { display: none; }
      .mpat-expand, .mpat-grip { display: grid; width: 27px; height: 27px; place-items: center; border: 0; border-radius: 8px; color: #6e7f93; background: transparent; }
      .mpat-expand:hover:not(:disabled), .mpat-grip:hover { color: var(--mpat-blue); background: #e5eff7; }
      .mpat-expand svg { transition: transform .16s ease; }
      [aria-expanded="true"] > .mpat-node .mpat-expand svg { transform: rotate(90deg); }
      .mpat-grip { cursor: grab; }
      .mpat-grip:active { cursor: grabbing; }
      .mpat-node-main { display: flex; min-width: 0; align-items: center; gap: 9px; border: 0; padding: 0; text-align: left; background: transparent; }
      .mpat-avatar { display: grid; flex: 0 0 35px; height: 35px; place-items: center; border-radius: 11px; color: #fff; background: linear-gradient(145deg, var(--mpat-blue-dark), #1683bd); box-shadow: 0 5px 12px rgba(11,77,139,.16); font-size: 10px; font-weight: 900; }
      .mpat-avatar.is-large { flex-basis: 52px; height: 52px; border-radius: 15px; font-size: 14px; }
      .mpat-node-identity { min-width: 0; }
      .mpat-node-identity strong, .mpat-node-identity span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .mpat-node-identity strong { color: var(--mpat-ink); font-size: 11px; }
      .mpat-node-identity span { margin-top: 2px; color: var(--mpat-muted); font-size: 9px; }
      .mpat-node-counts { display: flex; gap: 5px; }
      .mpat-node-counts > span { min-width: 62px; border: 1px solid var(--mpat-line); border-radius: 9px; padding: 5px 7px; color: var(--mpat-muted); background: #fff; font-size: 8px; text-align: center; }
      .mpat-node-counts b { display: block; color: var(--mpat-ink); font-size: 10px; }
      .mpat-pill { display: inline-flex; align-items: center; justify-content: center; min-height: 23px; border-radius: 20px; padding: 3px 8px; font-size: 8px; font-weight: 850; text-transform: capitalize; white-space: nowrap; }
      .mpat-pill.is-success { color: var(--mpat-green); background: var(--mpat-green-soft); }
      .mpat-pill.is-danger { color: var(--mpat-red); background: var(--mpat-red-soft); }
      .mpat-pill.is-warning { color: #9a580e; background: var(--mpat-orange-soft); }
      .mpat-pill.is-neutral { color: #607186; background: #edf2f6; }
      .mpat-target-button { min-height: 30px; border: 1px solid #e3ad69; border-radius: 9px; padding: 0 9px; color: #995008; background: #fff6ea; font-size: 9px; font-weight: 850; white-space: nowrap; }
      .mpat-branch-empty { margin: 2px 0 7px calc(var(--mpat-depth) * 24px + 60px); color: var(--mpat-muted); font-size: 9px; }
      .mpat-load-more { display: block; min-height: 38px; margin: 12px auto; border: 1px solid #cbdce9; border-radius: 10px; padding: 0 14px; color: var(--mpat-blue); background: #f1f7fb; font-size: 10px; font-weight: 850; }
      .mpat-load-more.is-branch { margin-left: 70px; }
      .mpat-detail-panel { min-height: 420px; overflow: hidden; }
      .mpat-detail-header { padding: 17px; border-bottom: 1px solid var(--mpat-line); }
      .mpat-detail-header > .mpat-pill { margin-top: 11px; }
      .mpat-user-summary { display: flex; min-width: 0; align-items: center; gap: 11px; }
      .mpat-user-summary > div { min-width: 0; }
      .mpat-user-summary strong, .mpat-user-summary span, .mpat-user-summary small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .mpat-user-summary strong { font-size: 13px; }
      .mpat-user-summary span { margin-top: 2px; color: var(--mpat-muted); font-size: 10px; }
      .mpat-user-summary small { margin-top: 3px; color: #8a98a8; font-size: 8px; }
      .mpat-user-summary.is-compact strong { font-size: 11px; }
      .mpat-detail-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; padding: 12px 15px 0; }
      .mpat-detail-section { padding: 15px; border-bottom: 1px solid var(--mpat-line); }
      .mpat-detail-section:last-child { border-bottom: 0; }
      .mpat-detail-section h3 { margin: 0 0 10px; font-size: 11px; }
      .mpat-detail-section dl { display: grid; gap: 7px; margin: 0; }
      .mpat-detail-section dl > div { display: flex; justify-content: space-between; gap: 10px; }
      .mpat-detail-section dt { color: var(--mpat-muted); font-size: 9px; }
      .mpat-detail-section dd { margin: 0; max-width: 58%; font-size: 9px; font-weight: 800; text-align: right; }
      .mpat-title-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 10px; }
      .mpat-title-row h3 { margin: 0; }
      .mpat-copy-value { display: flex; width: 100%; align-items: center; justify-content: space-between; gap: 9px; border: 1px dashed #a9c6dd; border-radius: 11px; padding: 9px 10px; color: var(--mpat-blue); text-align: left; background: #f3f8fc; }
      .mpat-copy-value b, .mpat-copy-value small { display: block; }
      .mpat-copy-value b { font-size: 10px; }
      .mpat-copy-value small { margin-top: 2px; color: var(--mpat-muted); font-size: 8px; }
      .mpat-inline-warning { display: flex; gap: 7px; align-items: flex-start; margin: 0; border-radius: 10px; padding: 9px; color: #96530e; background: var(--mpat-orange-soft); font-size: 9px; }
      .mpat-detail-section.is-wallet { background: linear-gradient(145deg, #f3f8fc, #fff); }
      .mpat-readonly-mini { display: inline-flex; align-items: center; gap: 4px; color: var(--mpat-blue); font-size: 8px; font-weight: 850; }
      .mpat-balance { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 10px; }
      .mpat-balance > span { color: var(--mpat-muted); font-size: 9px; }
      .mpat-balance strong { color: var(--mpat-blue); font-size: 19px; }
      .mpat-balance strong small { font-size: 9px; }
      .mpat-wallet-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; margin-bottom: 9px; }
      .mpat-wallet-grid > div { border: 1px solid var(--mpat-line); border-radius: 9px; padding: 8px; background: #fff; }
      .mpat-wallet-grid span, .mpat-wallet-grid b { display: block; }
      .mpat-wallet-grid span { color: var(--mpat-muted); font-size: 8px; }
      .mpat-wallet-grid b { margin-top: 3px; font-size: 9px; }
      .mpat-wallet-notice { display: flex; align-items: center; gap: 5px; margin: 9px 0 0; color: var(--mpat-muted); font-size: 8px; }
      .mpat-muted-copy { color: var(--mpat-muted); font-size: 9px; }
      .mpat-view-card { margin: 16px clamp(14px, 3vw, 36px) 32px; overflow: hidden; }
      .mpat-health-list, .mpat-history-list { display: grid; gap: 9px; padding: 14px; }
      .mpat-health-item { display: grid; grid-template-columns: 43px minmax(220px, 1fr) auto auto; gap: 12px; align-items: center; border: 1px solid var(--mpat-line); border-left: 4px solid #e2ad65; border-radius: 13px; padding: 12px; }
      .mpat-health-item.is-danger { border-left-color: var(--mpat-red); }
      .mpat-health-icon { display: grid; width: 40px; height: 40px; place-items: center; border-radius: 11px; color: #9a580e; background: var(--mpat-orange-soft); }
      .mpat-health-item.is-danger .mpat-health-icon { color: var(--mpat-red); background: var(--mpat-red-soft); }
      .mpat-health-copy > span { color: var(--mpat-orange); font-size: 8px; font-weight: 900; text-transform: uppercase; }
      .mpat-health-copy strong { display: block; margin-top: 2px; font-size: 11px; }
      .mpat-health-copy p { margin: 3px 0 0; color: var(--mpat-muted); font-size: 9px; }
      .mpat-health-meta { text-align: right; }
      .mpat-health-meta small, .mpat-health-meta b { display: block; }
      .mpat-health-meta small { color: var(--mpat-muted); font-size: 9px; }
      .mpat-health-meta b { margin-top: 2px; font-size: 9px; }
      .mpat-history-item { display: grid; grid-template-columns: 46px minmax(0, 1fr) auto; gap: 12px; align-items: start; border: 1px solid var(--mpat-line); border-radius: 14px; padding: 13px; }
      .mpat-history-mark { display: grid; width: 44px; height: 44px; place-items: center; border-radius: 12px; color: var(--mpat-blue); background: var(--mpat-blue-soft); }
      .mpat-history-title { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; }
      .mpat-history-title strong { font-size: 11px; }
      .mpat-history-title > span { display: inline-flex; align-items: center; gap: 4px; color: var(--mpat-blue); font-size: 10px; font-weight: 800; }
      .mpat-history-main > p { margin: 5px 0 8px; color: var(--mpat-muted); font-size: 9px; }
      .mpat-history-details { display: flex; flex-wrap: wrap; gap: 7px 14px; color: var(--mpat-muted); font-size: 8px; }
      .mpat-history-details b { color: var(--mpat-ink); }
      .mpat-result-count { margin: 0; padding: 0 18px 16px; color: var(--mpat-muted); font-size: 9px; text-align: right; }
      .mpat-loading { display: flex; min-height: 180px; align-items: center; justify-content: center; gap: 9px; color: var(--mpat-muted); font-size: 11px; }
      .mpat-spinner { display: inline-block; width: 21px; height: 21px; border: 2px solid #d7e3ec; border-top-color: var(--mpat-blue); border-radius: 50%; animation: mpat-spin .7s linear infinite; }
      .mpat-spinner.is-small { width: 15px; height: 15px; }
      @keyframes mpat-spin { to { transform: rotate(360deg); } }
      .mpat-empty { display: grid; min-height: 260px; place-content: center; padding: 28px; text-align: center; }
      .mpat-empty > span { display: grid; width: 52px; height: 52px; place-items: center; margin: 0 auto 10px; border-radius: 15px; color: var(--mpat-blue); background: var(--mpat-blue-soft); }


.mpat-empty strong { font-size: 13px; }

.mpat-empty p {
  max-width: 420px;
  margin: 5px auto 0;
  color: var(--mpat-muted);
  font-size: 10px;
}


/* ============================================================
   TÜM EKİP GÖRÜNÜMÜ
   ============================================================ */

.mpat-team-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
  margin-bottom: 14px;
}

.mpat-team-summary > div {
  border: 1px solid var(--mpat-line);
  border-radius: 12px;
  padding: 11px 12px;
  background: #fafcfd;
}

.mpat-team-summary span,
.mpat-team-summary strong {
  display: block;
}

.mpat-team-summary span {
  color: var(--mpat-muted);
  font-size: 8px;
  font-weight: 850;
  letter-spacing: .45px;
}

.mpat-team-summary strong {
  margin-top: 4px;
  color: var(--mpat-blue-dark);
  font-size: 18px;
}

.mpat-team-list {
  display: grid;
  gap: 7px;
}

.mpat-team-row {
  display: grid;
  grid-template-columns:
    78px 42px minmax(0, 1fr)
    minmax(120px, .7fr) 70px;

  gap: 10px;
  align-items: center;
  width: 100%;
  border: 1px solid var(--mpat-line);
  border-radius: 12px;
  padding: 9px 10px;
  color: var(--mpat-ink);
  background: #fff;
  text-align: left;
  transition: .15s ease;
}

.mpat-team-row:hover {
  border-color: #b8d0e2;
  background: #f8fbfe;
  transform: translateY(-1px);
}

.mpat-team-level {
  display: inline-flex;
  justify-content: center;
  border-radius: 20px;
  padding: 5px 7px;
  color: var(--mpat-blue);
  background: var(--mpat-blue-soft);
  font-size: 8px;
  font-weight: 900;
}

.mpat-team-person strong,
.mpat-team-person small,
.mpat-team-sponsor small,
.mpat-team-sponsor b {
  display: block;
}

.mpat-team-person strong {
  font-size: 10px;
}

.mpat-team-person small,
.mpat-team-sponsor small {
  margin-top: 2px;
  color: var(--mpat-muted);
  font-size: 8px;
}

.mpat-team-sponsor b {
  margin-top: 2px;
  font-size: 9px;
}

.mpat-team-count {
  color: var(--mpat-muted);
  font-size: 8px;
  text-align: right;
}

.mpat-team-count b {
  display: block;
  color: var(--mpat-ink);
  font-size: 11px;
}


/* ============================================================
   GÜVENLİ SİLME
   ============================================================ */

.mpat-delete-warning {
  display: flex;
  gap: 10px;
  margin-top: 14px;
  border: 1px solid #efc3bf;
  border-radius: 12px;
  padding: 12px;
  color: #8f2b23;
  background: var(--mpat-red-soft);
}

.mpat-delete-warning.is-blocked {
  border-color: #efc58f;
  color: #8c500e;
  background: var(--mpat-orange-soft);
}

.mpat-delete-warning > svg {
  flex: 0 0 auto;
}

.mpat-delete-warning strong {
  font-size: 10px;
}

.mpat-delete-warning p {
  margin: 3px 0 0;
  font-size: 9px;
  line-height: 1.5;
}


.mpat-modal-backdrop {

 position: fixed; z-index: 10000; inset: 0; display: grid; place-items: center; padding: 18px; background: rgba(5,25,45,.68); backdrop-filter: blur(5px); }
      .mpat-modal { width: min(620px, 100%); max-height: calc(100vh - 36px); overflow: hidden; border-radius: 20px; background: #fff; box-shadow: 0 30px 90px rgba(0,0,0,.35); }
      .mpat-modal.is-large { width: min(900px, 100%); }
      .mpat-modal > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 14px; padding: 18px 20px; color: #fff; background: linear-gradient(115deg, var(--mpat-blue-dark), var(--mpat-blue)); }
      .mpat-modal > header h3 { margin: 0; font-size: 17px; }
      .mpat-modal > header p { margin: 4px 0 0; color: #d8e8f4; font-size: 10px; }
      .mpat-modal > header button { display: grid; flex: 0 0 34px; height: 34px; place-items: center; border: 0; border-radius: 10px; color: #fff; background: rgba(255,255,255,.12); }
      .mpat-modal-body { max-height: calc(100vh - 112px); overflow-y: auto; padding: 20px; }
      .mpat-inline-error { display: flex; gap: 8px; margin-bottom: 13px; border: 1px solid #edc3bf; border-radius: 11px; padding: 10px; color: #8f2b23; background: var(--mpat-red-soft); font-size: 10px; }
      .mpat-move-people { display: grid; grid-template-columns: 1fr auto 1fr; gap: 13px; align-items: center; }
      .mpat-move-people > div { min-width: 0; border: 1px solid var(--mpat-line); border-radius: 13px; padding: 12px; }
      .mpat-move-people > div > span { display: block; margin-bottom: 8px; color: var(--mpat-orange); font-size: 8px; font-weight: 900; letter-spacing: .6px; }
      .mpat-move-arrow { display: grid; width: 37px; height: 37px; place-items: center; border-radius: 50%; color: var(--mpat-blue); background: var(--mpat-blue-soft); }
      .mpat-preview-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-top: 13px; }
      .mpat-preview-grid > div { border: 1px solid var(--mpat-line); border-radius: 11px; padding: 10px; background: #fafcfd; }
      .mpat-preview-grid span, .mpat-preview-grid strong { display: block; }
      .mpat-preview-grid span { color: var(--mpat-muted); font-size: 8px; }
      .mpat-preview-grid strong { margin-top: 4px; font-size: 10px; }
      .mpat-preview-grid strong.is-success { display: flex; align-items: center; gap: 4px; color: var(--mpat-green); }
      .mpat-unchanged { display: flex; gap: 10px; margin-top: 13px; border: 1px solid #cce5da; border-radius: 12px; padding: 11px; color: #246c53; background: var(--mpat-green-soft); }
      .mpat-unchanged.is-warning { border-color: #efc58f; color: #8c500e; background: var(--mpat-orange-soft); }
      .mpat-unchanged > svg { flex: 0 0 auto; }
      .mpat-unchanged strong { font-size: 10px; }
      .mpat-unchanged p { margin: 3px 0 0; font-size: 9px; }
      .mpat-warnings { margin-top: 12px; border: 1px solid #efc58f; border-radius: 11px; padding: 10px; color: #8c500e; background: #fff8ef; }
      .mpat-warnings > strong { font-size: 10px; }
      .mpat-warnings p { display: flex; gap: 6px; margin: 6px 0 0; font-size: 9px; }
      .mpat-field { display: grid; gap: 6px; margin-top: 13px; }
      .mpat-field > span { color: #52647a; font-size: 10px; font-weight: 850; }
      .mpat-field > span b { color: var(--mpat-red); }
      .mpat-field input, .mpat-field textarea { width: 100%; border: 1px solid var(--mpat-line); border-radius: 10px; padding: 10px 11px; color: var(--mpat-ink); outline: 0; }
      .mpat-field input { height: 42px; }
      .mpat-field textarea { resize: vertical; min-height: 82px; }
      .mpat-field input:focus, .mpat-field textarea:focus { border-color: #7eadd2; box-shadow: 0 0 0 3px rgba(11,77,139,.08); }
      .mpat-field small { color: var(--mpat-muted); font-size: 8px; }
      .mpat-modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 16px; border-top: 1px solid var(--mpat-line); padding-top: 15px; }
      .mpat-toast { position: fixed; z-index: 11000; top: 18px; right: 18px; display: flex; width: min(430px, calc(100vw - 36px)); align-items: center; gap: 9px; border: 1px solid var(--mpat-line); border-left: 4px solid var(--mpat-green); border-radius: 13px; padding: 12px; background: #fff; box-shadow: 0 18px 50px rgba(5,25,45,.22); }
      .mpat-toast.is-error { border-left-color: var(--mpat-red); }
      .mpat-toast.is-info { border-left-color: var(--mpat-blue); }
      .mpat-toast > span { display: grid; flex: 0 0 30px; height: 30px; place-items: center; border-radius: 9px; color: var(--mpat-green); background: var(--mpat-green-soft); }
      .mpat-toast.is-error > span { color: var(--mpat-red); background: var(--mpat-red-soft); }
      .mpat-toast.is-info > span { color: var(--mpat-blue); background: var(--mpat-blue-soft); }
      .mpat-toast p { min-width: 0; flex: 1; margin: 0; font-size: 10px; font-weight: 750; }
      .mpat-toast > button { display: grid; width: 27px; height: 27px; place-items: center; border: 0; color: var(--mpat-muted); background: transparent; }
      @media (max-width: 1280px) {
        .mpat-stats { grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .mpat-workspace { grid-template-columns: 220px minmax(470px, 1fr); }
        .mpat-detail-panel { position: static; grid-column: 1 / -1; display: grid; grid-template-columns: repeat(3, 1fr); }
        .mpat-detail-header, .mpat-detail-actions { grid-column: 1 / -1; }
        .mpat-detail-section { border-right: 1px solid var(--mpat-line); }
      }
      @media (max-width: 900px) {
        .mpat-hero { align-items: flex-start; flex-direction: column; }
        .mpat-hero-actions { justify-content: flex-start; }
        .mpat-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .mpat-workspace { grid-template-columns: 1fr; }
        .mpat-control-panel, .mpat-detail-panel { position: static; }
        .mpat-detail-panel { grid-template-columns: 1fr 1fr; }
        .mpat-health-item { grid-template-columns: 43px minmax(0, 1fr) auto; }
        .mpat-health-meta { grid-column: 2 / 3; text-align: left; }
        .mpat-history-item { grid-template-columns: 46px minmax(0, 1fr); }
        .mpat-history-item > .mpat-btn { grid-column: 2; justify-self: start; }
      }
      @media (max-width: 640px) {
        .mpat-hero { min-height: auto; padding: 25px 17px; }
        .mpat-hero-actions, .mpat-readonly-badge, .mpat-hero-actions .mpat-btn { width: 100%; }
        .mpat-stats { grid-template-columns: 1fr 1fr; gap: 8px; }
        .mpat-stat { padding: 10px; }
        .mpat-stat-icon { flex-basis: 36px; height: 36px; }
        .mpat-stat-copy strong { font-size: 18px; }
        .mpat-move-banner { align-items: flex-start; flex-wrap: wrap; }
        .mpat-move-banner > button { margin-left: 50px; }
        .mpat-workspace { padding-inline: 10px; }
        .mpat-control-panel { order: 2; }
        .mpat-tree-panel { order: 1; }
        .mpat-detail-panel { order: 3; display: block; }
        .mpat-detail-section { border-right: 0; }
        .mpat-section-heading.is-row { align-items: flex-start; flex-direction: column; }
        .mpat-section-heading select { width: 100%; }
        .mpat-health-item { grid-template-columns: 40px minmax(0, 1fr); }
        .mpat-health-meta, .mpat-health-item > .mpat-btn { grid-column: 2; justify-self: start; }
        .mpat-history-item { grid-template-columns: 38px minmax(0, 1fr); }
        .mpat-history-mark { width: 38px; height: 38px; }
       
.mpat-history-title {
  align-items: flex-start;
  flex-direction: column;
}

.mpat-team-summary {
  grid-template-columns: 1fr;
}

.mpat-team-row {
  grid-template-columns: 66px 38px minmax(0, 1fr);
}

.mpat-team-sponsor,
.mpat-team-count {
  grid-column: 3;
  text-align: left;
}

.mpat-modal-backdrop {

 align-items: end; padding: 0; }
        .mpat-modal, .mpat-modal.is-large { max-height: 96vh; border-radius: 20px 20px 0 0; }
        .mpat-modal-body { padding: 15px; }
        .mpat-move-people { grid-template-columns: 1fr; }
        .mpat-move-arrow { margin: -4px auto; transform: rotate(90deg); }
        .mpat-preview-grid { grid-template-columns: 1fr 1fr; }
        .mpat-modal-actions { flex-direction: column-reverse; }
        .mpat-modal-actions .mpat-btn { width: 100%; }
      }
      @media (prefers-reduced-motion: reduce) {
        .mpat-shell *, .mpat-shell *::before, .mpat-shell *::after { scroll-behavior: auto !important; transition: none !important; }
      }
    `}</style>
  );
}
